import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import { useTimezone } from './contexts/TimezoneContext';
import ReleaseManager from "./components/ReleaseManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_ROUTE = "admin-mgmt-7f84b"; // Obfuscated admin route

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { formatTimestamp } = useTimezone();
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEulaModal, setShowEulaModal] = useState(false);
  const [eulaUser, setEulaUser] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Analytics filters
  const [dateRange, setDateRange] = useState("30d");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  
  // Activity logs filters  
  const [logSearchUser, setLogSearchUser] = useState("");
  const [logStartDate, setLogStartDate] = useState("");
  const [logEndDate, setLogEndDate] = useState("");
  const [logActivityType, setLogActivityType] = useState("");
  
  // Activity summary filters (same as analytics)
  const [summaryDateRange, setSummaryDateRange] = useState("30d");
  const [summaryStartDate, setSummaryStartDate] = useState("");
  const [summaryEndDate, setSummaryEndDate] = useState("");
  
  // Rewards management
  const [rewardsData, setRewardsData] = useState(null);
  const [pointsAdjustment, setPointsAdjustment] = useState({ user_id: "", points: "", reason: "" });
  const [allNonAdminUsers, setAllNonAdminUsers] = useState([]); // For dropdown
  
  // Transaction Management
  const [transactions, setTransactions] = useState([]);
  const [transactionSearch, setTransactionSearch] = useState("");
  const [transactionServiceFilter, setTransactionServiceFilter] = useState("ALL");
  const [transactionDateStart, setTransactionDateStart] = useState("");
  const [transactionDateEnd, setTransactionDateEnd] = useState("");
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [selectedStripeTransaction, setSelectedStripeTransaction] = useState(null);
  const [refundData, setRefundData] = useState({
    adminName: "",
    refundReason: "",
    confirmed: false
  });
  
  // Revenue analytics
  const [revenueData, setRevenueData] = useState(null);
  
  // Onboarding analytics
  const [onboardingData, setOnboardingData] = useState(null);
  const [revenueDateRange, setRevenueDateRange] = useState("30d");
  const [revenueStartDate, setRevenueStartDate] = useState("");
  const [revenueEndDate, setRevenueEndDate] = useState("");
  
  // API Cost Monitoring
  const [apiCostsData, setApiCostsData] = useState(null);
  const [apiCostDateRange, setApiCostDateRange] = useState("30d");
  const [apiCostStartDate, setApiCostStartDate] = useState("");
  const [apiCostEndDate, setApiCostEndDate] = useState("");
  const [loadingApiCosts, setLoadingApiCosts] = useState(false);
  
  // User management
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showUserTransactionsModal, setShowUserTransactionsModal] = useState(false);
  const [selectedUserTransactions, setSelectedUserTransactions] = useState([]);
  const [selectedUserForTransactions, setSelectedUserForTransactions] = useState(null);
  const [loadingUserTransactions, setLoadingUserTransactions] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    role: "free_user",
    subscription_status: "None",
    temporary_password: ""
  });

  // Check admin access
  useEffect(() => {
    if (!user || (user.role !== "admin" && !user.is_admin_designated)) {
      toast.error("Admin access required");
      navigate("/homepage");
      return;
    }
    loadAnalytics();
    loadApiCosts();
    loadRevenueData(); // Load revenue data on mount
  }, [user]);

  useEffect(() => {
    if (activeTab === "transactions") {
      loadTransactions();
    }
  }, [activeTab, transactionSearch, transactionServiceFilter, transactionDateStart, transactionDateEnd]);

  const loadAnalytics = async (customRange, startDate, endDate) => {
    try {
      setLoading(true);
      const params = {};
      
      if (customRange) {
        params.date_range = customRange;
      } else if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.date_range = dateRange;
      }
      
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/analytics/overview`, { 
        params,
        withCredentials: true 
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
      console.error("Analytics error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page = 1, search = "") => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/users`, { 
        params,
        withCredentials: true 
      });
      
      setUsers(response.data.users);
      setCurrentPage(response.data.pagination.current_page);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error("Failed to load users");
      console.error("Users error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (transactionSearch) params.search = transactionSearch;
      if (transactionServiceFilter && transactionServiceFilter !== 'ALL') params.service = transactionServiceFilter;
      if (transactionDateStart) params.start_date = transactionDateStart;
      if (transactionDateEnd) params.end_date = transactionDateEnd;
      
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/transactions`, {
        params,
        withCredentials: true
      });
      
      setTransactions(response.data.transactions || []);
    } catch (error) {
      toast.error("Failed to load transactions");
      console.error("Transactions error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async (page = 1) => {
    try {
      const params = { page, limit: 50 };
      
      if (logSearchUser) params.search_user = logSearchUser;
      if (logStartDate) params.start_date = logStartDate;
      if (logEndDate) params.end_date = logEndDate;
      if (logActivityType) params.activity_type = logActivityType;
      
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/activity-logs`, {
        params,
        withCredentials: true
      });
      setActivityLogs(response.data.logs);
    } catch (error) {
      toast.error("Failed to load activity logs");
      console.error("Activity logs error:", error);
    }
  };

  const loadApiCosts = async (customRange, startDate, endDate) => {
    try {
      setLoadingApiCosts(true);
      const params = {};
      
      if (customRange) {
        params.date_range = customRange;
      } else if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.date_range = apiCostDateRange;
      }
      
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/api-costs`, { 
        params,
        withCredentials: true 
      });
      setApiCostsData(response.data);
    } catch (error) {
      toast.error("Failed to load API cost data");
      console.error("API costs error:", error);
    } finally {
      setLoadingApiCosts(false);
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/${ADMIN_ROUTE}/users/${userId}`, updates, {
        withCredentials: true
      });
      toast.success("User updated successfully");
      loadUsers(currentPage, userSearch);
      setShowUserModal(false);
    } catch (error) {
      toast.error("Failed to update user");
      console.error("Update user error:", error);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/${ADMIN_ROUTE}/users/${userId}`, {
        withCredentials: true
      });
      toast.success("User deleted successfully");
      loadUsers(currentPage, userSearch);
    } catch (error) {
      toast.error("Failed to delete user");
      console.error("Delete user error:", error);
    }
  };

  const archiveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to archive this user?")) {
      return;
    }
    
    try {
      await axios.post(`${API}/${ADMIN_ROUTE}/users/${userId}/archive`, {}, {
        withCredentials: true
      });
      toast.success("User archived successfully");
      loadUsers(currentPage, userSearch);
    } catch (error) {
      toast.error("Failed to archive user");
      console.error("Archive user error:", error);
    }
  };

  const designateAdmin = async (userId) => {
    if (!window.confirm("Grant admin privileges to this user?")) {
      return;
    }
    
    try {
      await axios.post(`${API}/${ADMIN_ROUTE}/users/${userId}/designate-admin`, {}, {
        withCredentials: true
      });
      toast.success("Admin privileges granted");
      loadUsers(currentPage, userSearch);
    } catch (error) {
      toast.error("Failed to grant admin privileges");
      console.error("Designate admin error:", error);
    }
  };

  const masterReset = async () => {
    const confirmation1 = prompt(
      "⚠️ DANGER: MASTER RESET ⚠️\n\n" +
      "This will PERMANENTLY DELETE ALL user data except admin accounts.\n" +
      "This action CANNOT BE UNDONE.\n\n" +
      "Type 'CONFIRM_RESET_ALL_DATA' to proceed:"
    );
    
    if (confirmation1 !== "CONFIRM_RESET_ALL_DATA") {
      toast.info("Master reset cancelled");
      return;
    }
    
    const confirmation2 = prompt(
      "⚠️ FINAL WARNING ⚠️\n\n" +
      "You are about to delete ALL user data permanently.\n" +
      "This includes all users, BCTG results, and activity logs.\n\n" +
      "Type 'I_UNDERSTAND_THIS_CANNOT_BE_UNDONE' to confirm:"
    );
    
    if (confirmation2 !== "I_UNDERSTAND_THIS_CANNOT_BE_UNDONE") {
      toast.info("Master reset cancelled at final confirmation");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/${ADMIN_ROUTE}/master-reset`, {
        confirm_reset_1: confirmation1,
        confirm_reset_2: confirmation2
      }, { withCredentials: true });
      
      toast.success(`Master reset completed. Deleted: ${response.data.deleted_records.users} users, ${response.data.deleted_records.bctg_results} BCTG results. Logging out...`);
      
      // Clear all cached data
      if (response.data.clear_cache) {
        // Clear browser storage
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear any service worker cache if present
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
      }
      
      // Check if logout is required
      if (response.data.require_logout) {
        // Wait a moment for the toast to be visible
        setTimeout(() => {
          // Force full page reload to clear React state and redirect to login
          window.location.replace('/login');
        }, 2000);
      } else {
        // Reload data if no logout required
        loadAnalytics();
        if (users.length > 0) loadUsers();
        if (activityLogs.length > 0) loadActivityLogs();
      }
      
    } catch (error) {
      toast.error("Master reset failed: " + (error.response?.data?.detail || error.message));
      console.error("Master reset error:", error);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadUsers(1, userSearch);
  };

  const loadRewardsData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/rewards/overview`, {
        withCredentials: true
      });
      setRewardsData(response.data);
      
      // Load all non-admin users for the dropdown
      loadAllNonAdminUsers();
    } catch (error) {
      toast.error("Failed to load rewards data");
      console.error("Rewards data error:", error);
      // Set fallback data structure to prevent UI from disappearing
      setRewardsData({
        overview: {
          total_points_issued: 0,
          total_points_redeemed: 0,
          total_dollar_value: 0,
          successful_referrals: 0,
          pending_redemptions: 0
        },
        top_earners: [],
        recent_transactions: [],
        pending_redemptions: []
      });
    } finally {
      setLoading(false);
    }
  };
  
  const loadAllNonAdminUsers = async () => {
    try {
      // Fetch all users without pagination limit, excluding admins
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/users`, { 
        params: { limit: 1000, exclude_admins: true }, // Get up to 1000 non-admin users
        withCredentials: true 
      });
      
      // Filter out admins on the frontend as well for extra safety
      const nonAdmins = response.data.users.filter(u => 
        u.role !== 'admin' && !u.is_admin_designated
      );
      
      setAllNonAdminUsers(nonAdmins);
    } catch (error) {
      console.error("Failed to load users for dropdown:", error);
      toast.error("Failed to load user list");
    }
  };

  const loadRevenueData = async () => {
    try {
      setLoading(true);
      const params = { date_range: revenueDateRange };
      
      if (revenueDateRange === "custom" && revenueStartDate && revenueEndDate) {
        params.start_date = revenueStartDate;
        params.end_date = revenueEndDate;
      }
      
      const response = await axios.get(`${API}/admin/revenue-analytics`, {
        params,
        withCredentials: true
      });
      
      setRevenueData(response.data);
    } catch (error) {
      toast.error("Failed to load revenue analytics");
      console.error("Revenue data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadOnboardingData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/onboarding-analytics`, {
        withCredentials: true
      });
      setOnboardingData(response.data);
    } catch (error) {
      toast.error("Failed to load onboarding analytics");
      console.error("Onboarding data error:", error);
    } finally {
      setLoading(false);
    }
  };

  // User management functions
  const createUser = async () => {
    try {
      if (!newUser.email || !newUser.first_name || !newUser.last_name || !newUser.temporary_password) {
        toast.error("Please fill in all required fields including temporary password");
        return;
      }
      
      // Validate password strength
      if (newUser.temporary_password.length < 8) {
        toast.error("Temporary password must be at least 8 characters");
        return;
      }

      await axios.post(`${API}/${ADMIN_ROUTE}/users/create`, newUser, {
        withCredentials: true
      });
      
      toast.success(`User created successfully! Temporary password: ${newUser.temporary_password}`);
      setShowCreateUserModal(false);
      setNewUser({
        email: "",
        first_name: "",
        last_name: "",
        phone: "",
        role: "free_user",
        subscription_status: "None",
        temporary_password: ""
      });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const editUser = (user) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  const viewUserTransactions = async (user) => {
    try {
      setSelectedUserForTransactions(user);
      setLoadingUserTransactions(true);
      setShowUserTransactionsModal(true);
      
      const response = await axios.get(`${API}/payments/transactions?user_id=${user.id}`, {
        withCredentials: true
      });
      
      setSelectedUserTransactions(response.data.transactions || []);
    } catch (error) {
      console.error("Error loading user transactions:", error);
      toast.error("Failed to load user transactions");
      setSelectedUserTransactions([]);
    } finally {
      setLoadingUserTransactions(false);
    }
  };

  const handleDatabaseReset = async () => {
    if (resetConfirmText !== 'RESET DATABASE') {
      toast.error('Please type "RESET DATABASE" exactly to confirm');
      return;
    }

    try {
      setResetting(true);
      const response = await axios.post(`${API}/debug/reset-database`, {}, {
        withCredentials: true
      });

      if (response.data.success) {
        const deleted = response.data.deleted;
        const verification = response.data.verification;
        
        // Log verification data
        
        toast.success(
          `Database reset complete! All data cleared.\n` +
          `Collections processed: ${response.data.collections_processed}\n` +
          `Refreshing page...`,
          { duration: 5000 }
        );
        
        setShowResetModal(false);
        setResetConfirmText('');
        
        // Force hard refresh to clear all cached data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error("Database reset error:", error);
      toast.error(error.response?.data?.detail || "Failed to reset database");
    } finally {
      setResetting(false);
    }
  };

  const updateUserProfile = async () => {
    try {
      if (!selectedUser.email || !selectedUser.first_name || !selectedUser.last_name) {
        toast.error("Please fill in all required fields");
        return;
      }

      await axios.put(`${API}/${ADMIN_ROUTE}/users/${selectedUser.id}`, selectedUser, {
        withCredentials: true
      });
      
      toast.success("User updated successfully");
      setShowEditUserModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const adjustUserPoints = async () => {
    if (!pointsAdjustment.user_id || !pointsAdjustment.points || !pointsAdjustment.reason) {
      toast.error("Please fill in all fields");
      return;
    }
    
    try {
      await axios.post(`${API}/${ADMIN_ROUTE}/rewards/adjust-points`, {
        user_id: pointsAdjustment.user_id,
        points: parseInt(pointsAdjustment.points),
        reason: pointsAdjustment.reason
      }, { withCredentials: true });
      
      toast.success("Points adjusted successfully");
      setPointsAdjustment({ user_id: "", points: "", reason: "" });
      loadRewardsData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adjust points");
    }
  };

  // Transaction Management Functions
  const handleResendReceipt = async (transaction) => {
    try {
      await axios.post(`${API}/send-receipt-email`, {
        transaction_id: transaction.id,
        user_email: transaction.user_email
      }, { withCredentials: true });
      
      toast.success("Receipt email resent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend receipt");
    }
  };

  const handlePrintTransaction = (transaction) => {
    // Create printable receipt HTML
    const receiptHTML = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h2 style="text-align: center; color: #1e40af;">Digital Ink AI</h2>
        <h3 style="text-align: center;">Transaction Receipt</h3>
        <hr style="margin: 20px 0;" />
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px; font-weight: bold;">Date:</td>
            <td style="padding: 8px;">${transaction.created_at_display || formatTimestamp(transaction.created_at)}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Customer:</td>
            <td style="padding: 8px;">${transaction.user_name}<br/>${transaction.user_email}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Service:</td>
            <td style="padding: 8px;">${transaction.service}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Amount:</td>
            <td style="padding: 8px; font-size: 18px; color: #059669;">$${(transaction.amount || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Status:</td>
            <td style="padding: 8px;">${transaction.payment_status || 'completed'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; font-weight: bold;">Transaction ID:</td>
            <td style="padding: 8px; font-family: monospace; font-size: 12px;">${transaction.id}</td>
          </tr>
        </table>
        <hr style="margin: 20px 0;" />
        <p style="text-align: center; color: #64748b; font-size: 12px;">
          Thank you for using Digital Ink AI
        </p>
      </div>
    `;

    // Create temporary element for PDF generation
    const element = document.createElement('div');
    element.innerHTML = receiptHTML;
    
    const opt = {
      margin: 0.5,
      filename: `transaction-${transaction.id.substring(0, 8)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const handleRefund = async () => {
    if (!refundData.adminName || !refundData.refundReason) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!refundData.confirmed) {
      toast.error("Please confirm the refund");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/${ADMIN_ROUTE}/refund-transaction`, {
        transaction_id: selectedTransaction.id,
        admin_name: refundData.adminName,
        refund_reason: refundData.refundReason
      }, { withCredentials: true });

      toast.success("Refund processed successfully");
      setShowRefundModal(false);
      setRefundData({ adminName: "", refundReason: "", confirmed: false });
      setSelectedTransaction(null);
      loadTransactions(); // Reload transactions list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process refund");
    } finally {
      setLoading(false);
    }
  };

  // Use central MST formatter
  
  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-800",
      beta_tester: "bg-purple-100 text-purple-800", 
      student: "bg-green-100 text-green-800",
      subscriber: "bg-blue-100 text-blue-800",
      a_la_carte: "bg-yellow-100 text-yellow-800",
      free_user: "bg-gray-100 text-gray-800",
      archived: "bg-red-100 text-red-600"
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/homepage')}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
                  alt="Digital Ink AI" 
                  className="h-10 w-auto hover:opacity-80 transition-opacity"
                />
              </div>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-xl font-bold text-slate-900">Admin Analytics</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/homepage')}
                className="text-slate-600 hover:text-slate-800 border-slate-300"
              >
                ← Return to Home
              </Button>
              <Badge variant="destructive">ANALYTICS</Badge>
              <span className="text-sm text-slate-600">{user?.first_name} {user?.last_name}</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Tab Navigation - Streamlined */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "overview", name: "Dashboard", icon: "📊" },
              { id: "users", name: "Users", icon: "👥" }, 
              { id: "revenue", name: "Revenue", icon: "💰" },
              { id: "rewards", name: "Rewards", icon: "🎁" },
              { id: "transactions", name: "Transactions", icon: "💳" },
              { id: "activity_logs", name: "Logs", icon: "📋" },
              { id: "onboarding", name: "Onboarding", icon: "📝" },
              { id: "activity_summary", name: "Activity", icon: "📈" },
              { id: "release_manager", name: "Releases", icon: "🚀" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  if (tab.id === "users" && users.length === 0) loadUsers();
                  if ((tab.id === "activity_logs" || tab.id === "activity_summary") && activityLogs.length === 0) loadActivityLogs();
                  if (tab.id === "overview") loadAnalytics();
                  if (tab.id === "rewards") loadRewardsData();
                  if (tab.id === "revenue") loadRevenueData();
                  if (tab.id === "onboarding") loadOnboardingData();
                }}
                className={`${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-white text-slate-700 hover:bg-blue-50 border border-slate-200"
                } rounded-lg py-2 px-4 font-medium text-sm transition-all duration-200 flex items-center gap-2`}
              >
                <span className="text-xl">{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === "overview" && analytics && (
          <div className="space-y-6">
            {/* Date Filter Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Analytics Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <Label>Date Range</Label>
                    <select 
                      className="w-full p-2 border rounded mt-1"
                      value={dateRange}
                      onChange={(e) => {
                        setDateRange(e.target.value);
                        if (e.target.value !== "custom") {
                          loadAnalytics(e.target.value);
                        }
                      }}
                    >
                      <option value="7d">Last 7 days</option>
                      <option value="30d">Last 30 days</option>
                      <option value="90d">Last 90 days</option>
                      <option value="1y">Last year</option>
                      <option value="lifetime">All time</option>
                      <option value="custom">Custom range</option>
                    </select>
                  </div>
                  
                  {dateRange === "custom" && (
                    <>
                      <div>
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <Button 
                        onClick={() => {
                          if (customStartDate && customEndDate) {
                            loadAnalytics(null, customStartDate, customEndDate);
                          }
                        }}
                        disabled={!customStartDate || !customEndDate}
                      >
                        Apply Filter
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* ========== AI-POWERED INSIGHTS & PATTERN DETECTION ========== */}
            <Card className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">🤖</span>
                  <span>AI-Powered Insights & Pattern Detection</span>
                  <Badge className="ml-2 bg-purple-600">Real-Time Analysis</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(analytics.user_metrics.total_users > 1 || (revenueData?.totals?.revenue || 0) > 0) ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Growth Trend Analysis */}
                    {analytics.user_metrics.period_users > 0 && (
                      <div className={`bg-white p-4 rounded-lg border-l-4 ${analytics.user_metrics.period_users >= 5 ? 'border-green-500' : 'border-blue-500'}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-2xl">{analytics.user_metrics.period_users >= 5 ? '🚀' : '📈'}</span>
                          <div>
                            <div className={`font-bold ${analytics.user_metrics.period_users >= 5 ? 'text-green-900' : 'text-blue-900'}`}>
                              {analytics.user_metrics.period_users >= 5 ? 'Rapid Growth Phase' : 'Growth Tracked'}
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                              {analytics.user_metrics.period_users} new users
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {((analytics.user_metrics.period_users / analytics.user_metrics.total_users) * 100).toFixed(1)}% growth rate
                            </div>
                          </div>
                        </div>
                        <div className={`text-xs mt-2 ${analytics.user_metrics.period_users >= 5 ? 'text-green-700' : 'text-blue-700'} font-medium`}>
                          {analytics.user_metrics.period_users >= 5 ? '→ Scale support & infrastructure' : '→ Monitor conversion funnel'}
                        </div>
                      </div>
                    )}

                    {/* Revenue Intelligence */}
                    {revenueData && revenueData.totals?.revenue > 0 ? (
                      <div className="bg-white p-4 rounded-lg border-l-4 border-emerald-500">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-2xl">💎</span>
                          <div>
                            <div className="font-bold text-emerald-900">Monetization Active</div>
                            <div className="text-sm text-slate-600 mt-1">
                              ${(revenueData.totals.revenue || 0).toFixed(2)} revenue
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {revenueData.totals.transactions || 0} transactions
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-emerald-700 font-medium mt-2">
                          → Avg: ${revenueData.totals.transactions > 0 ? (revenueData.totals.revenue / revenueData.totals.transactions).toFixed(2) : '0.00'} per transaction
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white p-4 rounded-lg border-l-4 border-amber-500">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-2xl">💡</span>
                          <div>
                            <div className="font-bold text-amber-900">Revenue Opportunity</div>
                            <div className="text-sm text-slate-600 mt-1">
                              {analytics.user_metrics.total_users} users, $0 revenue
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {analytics.user_metrics.active_7_days} active users ready to convert
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-amber-700 font-medium mt-2">
                          → Implement conversion strategies
                        </div>
                      </div>
                    )}

                    {/* Service Performance */}
                    {(() => {
                      const services = [
                        { name: 'BCTG', count: analytics.service_metrics?.bctg?.total_generations || 0, type: 'Premium' },
                        { name: 'MMKG', count: analytics.service_metrics?.mmkg?.total_kits || 0, type: 'Premium' },
                        { name: 'IBG', count: analytics.service_metrics?.ibg?.total_generations || 0, type: 'Free' }
                      ];
                      const topService = services.reduce((max, s) => s.count > max.count ? s : max);
                      
                      if (topService.count > 0) {
                        return (
                          <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500">
                            <div className="flex items-start gap-2 mb-2">
                              <span className="text-2xl">⭐</span>
                              <div>
                                <div className="font-bold text-purple-900">Top Service: {topService.name}</div>
                                <div className="text-sm text-slate-600 mt-1">
                                  {topService.count} uses ({topService.type})
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  {((topService.count / (services.reduce((sum, s) => sum + s.count, 0))) * 100).toFixed(1)}% of total usage
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-purple-700 font-medium mt-2">
                              {topService.type === 'Free' ? '→ Lead gen working - promote upgrades' : '→ Revenue driver - cross-sell others'}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Cost Efficiency */}
                    {apiCostsData?.summary?.total_cost > 0 && revenueData && revenueData.totals?.revenue > 0 && (
                      (() => {
                        const costRatio = (apiCostsData.summary.total_cost / revenueData.totals.revenue) * 100;
                        const merchantFees = (revenueData.totals.revenue * 0.029) + (revenueData.totals.transactions * 0.30);
                        const netMargin = ((revenueData.totals.revenue - apiCostsData.summary.total_cost - merchantFees) / revenueData.totals.revenue) * 100;
                        const isHealthy = costRatio < 20;
                        
                        return (
                          <div className={`bg-white p-4 rounded-lg border-l-4 ${isHealthy ? 'border-green-500' : 'border-red-500'}`}>
                            <div className="flex items-start gap-2 mb-2">
                              <span className="text-2xl">{isHealthy ? '✅' : '⚠️'}</span>
                              <div>
                                <div className={`font-bold ${isHealthy ? 'text-green-900' : 'text-red-900'}`}>
                                  {isHealthy ? 'Healthy Margins' : 'Cost Alert'}
                                </div>
                                <div className="text-sm text-slate-600 mt-1">
                                  API: {costRatio.toFixed(1)}% of revenue
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  Net margin: {netMargin.toFixed(1)}%
                                </div>
                              </div>
                            </div>
                            <div className={`text-xs ${isHealthy ? 'text-green-700' : 'text-red-700'} font-medium mt-2`}>
                              {isHealthy ? '→ Sustainable cost structure' : '→ Review high-cost services'}
                            </div>
                          </div>
                        );
                      })()
                    )}

                    {/* Engagement Pattern */}
                    {analytics.user_metrics.active_7_days > 0 && (
                      (() => {
                        const engagementRate = (analytics.user_metrics.active_7_days / analytics.user_metrics.total_users) * 100;
                        const isHighEngagement = engagementRate > 40;
                        
                        return (
                          <div className={`bg-white p-4 rounded-lg border-l-4 ${isHighEngagement ? 'border-cyan-500' : 'border-yellow-500'}`}>
                            <div className="flex items-start gap-2 mb-2">
                              <span className="text-2xl">{isHighEngagement ? '🔥' : '📊'}</span>
                              <div>
                                <div className={`font-bold ${isHighEngagement ? 'text-cyan-900' : 'text-yellow-900'}`}>
                                  {isHighEngagement ? 'High Engagement' : 'Engagement Tracking'}
                                </div>
                                <div className="text-sm text-slate-600 mt-1">
                                  {analytics.user_metrics.active_7_days} / {analytics.user_metrics.total_users} active
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  {engagementRate.toFixed(1)}% engagement rate
                                </div>
                              </div>
                            </div>
                            <div className={`text-xs ${isHighEngagement ? 'text-cyan-700' : 'text-yellow-700'} font-medium mt-2`}>
                              {isHighEngagement ? '→ High retention - build loyalty program' : '→ Improve onboarding & activation'}
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <span className="text-6xl mb-4 block">🤖</span>
                    <div className="text-lg font-semibold text-slate-700 mb-2">AI Insights Initializing</div>
                    <div className="text-sm text-slate-500 max-w-2xl mx-auto">
                      Advanced pattern detection and automated recommendations will appear here as your platform accumulates meaningful data. The AI will analyze user behavior, revenue patterns, service performance, cost efficiency, and engagement metrics to provide actionable intelligence.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* ========== CRITICAL HEALTH SNAPSHOT ========== */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Users */}
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-slate-600">Total Users</div>
                    <div className="text-2xl">👥</div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-1">
                    {analytics.user_metrics.total_users.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`font-medium ${analytics.user_metrics.period_users > 0 ? 'text-green-600' : 'text-slate-500'}`}>
                      {analytics.user_metrics.period_users > 0 ? '↑' : '→'} {analytics.user_metrics.period_users}
                    </span>
                    <span className="text-slate-500">in period</span>
                  </div>
                </CardContent>
              </Card>

              {/* Active Users (7 days) */}
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-slate-600">Active Users</div>
                    <div className="text-2xl">✨</div>
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-1">
                    {analytics.user_metrics.active_7_days.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">
                      {((analytics.user_metrics.active_7_days / analytics.user_metrics.total_users) * 100).toFixed(1)}% of total
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Total Revenue */}
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-slate-600">Total Revenue</div>
                    <div className="text-2xl">💰</div>
                  </div>
                  <div className="text-3xl font-bold text-emerald-600 mb-1">
                    ${(revenueData?.totals?.revenue || 0).toFixed(2)}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">
                      {revenueData?.totals?.transactions || 0} transaction{revenueData?.totals?.transactions !== 1 ? 's' : ''} in period
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Conversion Rate */}
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-slate-600">Conversion Rate</div>
                    <div className="text-2xl">📊</div>
                  </div>
                  <div className="text-3xl font-bold text-purple-600 mb-1">
                    {analytics.conversion_rate ? `${analytics.conversion_rate.toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">Free → Paid</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ========== CONVERSION FUNNEL ========== */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span>🎯</span> Conversion Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Funnel Stages */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {[
                      { label: 'Total Users', value: analytics.user_metrics.total_users, percentage: 100, color: 'bg-blue-500' },
                      { label: 'Free Tool Users', value: (analytics.service_metrics?.ibg?.total_generations || 0) + (analytics.service_metrics?.qrg?.total_generations || 0) + (analytics.service_metrics?.bctg_free?.total_generations || 0), percentage: 70, color: 'bg-cyan-500' },
                      { label: 'Active (7d)', value: analytics.user_metrics.active_7_days, percentage: 50, color: 'bg-green-500' },
                      { label: 'Paid Service', value: (analytics.user_metrics?.role_breakdown?.subscriber || 0) + (analytics.user_metrics?.role_breakdown?.a_la_carte || 0), percentage: 30, color: 'bg-amber-500' },
                      { label: 'Subscribers', value: analytics.user_metrics?.subscribers || 0, percentage: 15, color: 'bg-purple-500' }
                    ].map((stage, index) => (
                      <div key={index} className="relative">
                        <div className="text-center mb-2">
                          <div className="text-2xl font-bold text-slate-900">{stage.value.toLocaleString()}</div>
                          <div className="text-xs text-slate-600 font-medium">{stage.label}</div>
                          <div className="text-xs text-slate-500">{stage.percentage}%</div>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div className={`${stage.color} h-2 rounded-full`} style={{width: `${stage.percentage}%`}}></div>
                        </div>
                        {index < 4 && (
                          <div className="absolute top-1/2 -right-2 text-slate-400 text-xl hidden md:block">→</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ========== REVENUE INTELLIGENCE & SERVICE PERFORMANCE ========== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Revenue Intelligence - REAL DATA */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>💎</span> Revenue Intelligence
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                      <span className="text-sm font-medium text-slate-700">Avg Transaction Value</span>
                      <span className="text-lg font-bold text-emerald-600">
                        ${revenueData && revenueData.totals?.transactions > 0 
                          ? (revenueData.totals.revenue / revenueData.totals.transactions).toFixed(2) 
                          : '0.00'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium text-slate-700">Revenue per User</span>
                      <span className="text-lg font-bold text-blue-600">
                        ${revenueData && analytics.user_metrics?.total_users > 0
                          ? ((revenueData.totals?.revenue || 0) / analytics.user_metrics.total_users).toFixed(2)
                          : '0.00'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="text-sm font-medium text-slate-700">Paying Users</span>
                      <span className="text-lg font-bold text-purple-600">
                        {(() => {
                          const payingUsers = (analytics.user_metrics?.role_breakdown?.subscriber || 0) + 
                                            (analytics.user_metrics?.role_breakdown?.a_la_carte || 0);
                          const totalUsers = analytics.user_metrics?.total_users || 0;
                          const percentage = totalUsers > 0 ? ((payingUsers / totalUsers) * 100).toFixed(1) : '0.0';
                          return `${payingUsers} (${percentage}%)`;
                        })()}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions Hub */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>⚡</span> Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      onClick={() => setActiveTab('users')}
                      variant="outline"
                      className="flex flex-col items-center gap-2 h-auto py-4"
                    >
                      <span className="text-2xl">👤</span>
                      <span className="text-sm">Manage Users</span>
                    </Button>
                    <Button 
                      onClick={() => setActiveTab('rewards')}
                      variant="outline"
                      className="flex flex-col items-center gap-2 h-auto py-4"
                    >
                      <span className="text-2xl">💎</span>
                      <span className="text-sm">Adjust Points</span>
                    </Button>
                    <Button 
                      onClick={() => setActiveTab('transactions')}
                      variant="outline"
                      className="flex flex-col items-center gap-2 h-auto py-4"
                    >
                      <span className="text-2xl">💳</span>
                      <span className="text-sm">Transactions</span>
                    </Button>
                    <Button 
                      onClick={() => setActiveTab('revenue')}
                      variant="outline"
                      className="flex flex-col items-center gap-2 h-auto py-4"
                    >
                      <span className="text-2xl">📊</span>
                      <span className="text-sm">Revenue Report</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* ========== SERVICE PERFORMANCE MATRIX ========== */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span>⭐</span> Service Performance Matrix
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Stars: High Usage, High Revenue */}
                  <div className="p-4 border-2 border-green-200 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">⭐</span>
                      <h4 className="font-bold text-green-900">Stars</h4>
                      <span className="text-xs text-green-700">(High Usage · High Revenue)</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">BCTG</span>
                        <span className="text-green-700">{analytics.service_metrics?.bctg?.total_generations || 0} uses</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">MMKG</span>
                        <span className="text-green-700">{analytics.service_metrics?.mmkg?.total_kits || 0} uses</span>
                      </div>
                    </div>
                  </div>

                  {/* Premium: Low Usage, High Revenue */}
                  <div className="p-4 border-2 border-purple-200 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">💎</span>
                      <h4 className="font-bold text-purple-900">Premium</h4>
                      <span className="text-xs text-purple-700">(Low Usage · High Revenue)</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">KSG</span>
                        <span className="text-purple-700">{analytics.service_metrics?.ksg?.total_speeches || 0} uses</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">CCG</span>
                        <span className="text-purple-700">{analytics.service_metrics?.ccg?.total_casts || 0} uses</span>
                      </div>
                    </div>
                  </div>

                  {/* Growth: High Usage, Low Revenue */}
                  <div className="p-4 border-2 border-blue-200 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">📈</span>
                      <h4 className="font-bold text-blue-900">Growth Potential</h4>
                      <span className="text-xs text-blue-700">(High Usage · Low Revenue)</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">IBG (Free)</span>
                        <span className="text-blue-700">{analytics.service_metrics?.ibg?.total_generations || 0} uses</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">QRG (Free)</span>
                        <span className="text-blue-700">{analytics.service_metrics?.qrg?.total_generations || 0} uses</span>
                      </div>
                    </div>
                  </div>

                  {/* Review: Low Usage, Low Revenue */}
                  <div className="p-4 border-2 border-amber-200 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">⚠️</span>
                      <h4 className="font-bold text-amber-900">Needs Review</h4>
                      <span className="text-xs text-amber-700">(Low Usage · Low Revenue)</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">SMMK</span>
                        <span className="text-amber-700">Coming Soon</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-slate-500">Monitor others</span>
                        <span className="text-amber-700">-</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ========== COMPLETE COST STRUCTURE & PROFITABILITY ========== */}
            <Card className="mb-8 border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span>💸</span> Complete Cost Structure & Net Profitability
                  <Badge className="ml-2 bg-amber-600">Real-time</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Top Level Metrics - Full Cost Breakdown - REAL DATA */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
                  {(() => {
                    const revenue = revenueData?.totals?.revenue || 0;
                    const transactions = revenueData?.totals?.transactions || 0;
                    const merchantFees = (revenue * 0.029) + (transactions * 0.30);
                    const apiCosts = apiCostsData?.summary?.total_cost || 0;
                    const totalCosts = merchantFees + apiCosts;
                    const netProfit = revenue - totalCosts;
                    const netMargin = revenue > 0 ? ((netProfit / revenue) * 100) : 0;
                    
                    return (
                      <>
                        <div className="text-center p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                          <div className="text-sm text-emerald-700 font-medium mb-2">Gross Revenue</div>
                          <div className="text-3xl font-bold text-emerald-600">
                            ${revenue.toFixed(2)}
                          </div>
                          <div className="text-xs text-emerald-600 mt-2">{transactions} txns</div>
                        </div>

                        <div className="text-center p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                          <div className="text-sm text-orange-700 font-medium mb-2">Merchant Fees</div>
                          <div className="text-3xl font-bold text-orange-600">
                            ${merchantFees.toFixed(2)}
                          </div>
                          <div className="text-xs text-orange-600 mt-2">2.9% + $0.30/txn</div>
                        </div>

                        <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-200">
                          <div className="text-sm text-red-700 font-medium mb-2">API Costs</div>
                          <div className="text-3xl font-bold text-red-600">
                            ${apiCosts.toFixed(2)}
                          </div>
                          <div className="text-xs text-red-600 mt-2">{apiCostsData?.summary?.total_api_calls || 0} calls</div>
                        </div>

                        <div className="text-center p-4 bg-rose-50 rounded-lg border-2 border-rose-200">
                          <div className="text-sm text-rose-700 font-medium mb-2">Total Costs</div>
                          <div className="text-3xl font-bold text-rose-600">
                            ${totalCosts.toFixed(2)}
                          </div>
                          <div className="text-xs text-rose-600 mt-2">{revenue > 0 ? ((totalCosts / revenue) * 100).toFixed(1) : '0'}%</div>
                        </div>

                        <div className="text-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                          <div className="text-sm text-blue-700 font-medium mb-2">Net Profit</div>
                          <div className={`text-3xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ${netProfit.toFixed(2)}
                          </div>
                          <div className="text-xs text-blue-600 mt-2">After costs</div>
                        </div>

                        <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
                          <div className="text-sm text-green-700 font-medium mb-2">Net Margin</div>
                          <div className={`text-3xl font-bold ${netMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {netMargin.toFixed(1)}%
                          </div>
                          <div className="text-xs text-green-600 mt-2">{netMargin >= 70 ? '✓ Excellent' : netMargin >= 50 ? '✓ Good' : netMargin >= 30 ? 'Fair' : 'Low'}</div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Revenue Waterfall Visualization - REAL DATA */}
                <div className="mb-6 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-3">Revenue Waterfall Analysis</h4>
                  {revenueData && apiCostsData && revenueData.totals?.revenue > 0 ? (
                    <div className="space-y-2">
                      {(() => {
                        const revenue = revenueData.totals.revenue;
                        const transactions = revenueData.totals.transactions;
                        const merchantFees = (revenue * 0.029) + (transactions * 0.30);
                        const apiCosts = apiCostsData.summary?.total_cost || 0;
                        const afterFees = revenue - merchantFees;
                        const netProfit = revenue - merchantFees - apiCosts;
                        
                        return (
                          <>
                            <div className="flex items-center gap-2">
                              <div className="w-32 text-xs text-slate-600 font-medium">Gross Revenue:</div>
                              <div className="flex-1 bg-emerald-500 h-8 rounded flex items-center justify-between px-3 text-white text-sm font-bold">
                                <span>${revenue.toFixed(2)}</span>
                                <span>100%</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-32 text-xs text-slate-600 font-medium">After Stripe Fees:</div>
                              <div style={{width: `${(afterFees / revenue * 100).toFixed(0)}%`}} className="bg-orange-400 h-8 rounded flex items-center justify-between px-3 text-white text-sm font-bold">
                                <span>${afterFees.toFixed(2)} (-${merchantFees.toFixed(2)})</span>
                                <span>{(afterFees / revenue * 100).toFixed(0)}%</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-32 text-xs text-slate-600 font-medium">Net Profit:</div>
                              <div style={{width: `${Math.max(5, (netProfit / revenue * 100)).toFixed(0)}%`}} className={`${netProfit >= 0 ? 'bg-green-400' : 'bg-red-400'} h-8 rounded flex items-center justify-between px-3 ${netProfit >= 0 ? 'text-green-900' : 'text-red-900'} text-sm font-bold`}>
                                <span>${netProfit.toFixed(2)} (-${apiCosts.toFixed(2)})</span>
                                <span>{(netProfit / revenue * 100).toFixed(0)}%</span>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-500 text-sm">
                      No revenue data available for waterfall analysis
                    </div>
                  )}
                </div>

                {/* Merchant Fee Details */}
                <div className="mb-6 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                  <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                    <span>💳</span> Merchant Transaction Fees Breakdown
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-orange-900 mb-2">Standard Stripe/PayPal Fees:</div>
                      <ul className="space-y-1 text-sm text-orange-800">
                        <li>• Percentage Fee: 2.9% per transaction</li>
                        <li>• Fixed Fee: $0.30 per transaction</li>
                        <li>• No monthly fees (pay-as-you-go)</li>
                      </ul>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-orange-900 mb-2">Period Calculations:</div>
                      <ul className="space-y-1 text-sm text-orange-800">
                        <li>• Total Transactions: {revenueData?.totals?.transactions || 0}</li>
                        <li>• Avg Transaction: ${revenueData && revenueData.totals?.transactions > 0 ? (revenueData.totals.revenue / revenueData.totals.transactions).toFixed(2) : '0.00'}</li>
                        <li>• Total Merchant Fees: ${revenueData ? ((revenueData.totals?.revenue || 0) * 0.029 + (revenueData.totals?.transactions || 0) * 0.30).toFixed(2) : '0.00'}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Service Cost Breakdown - REAL API COSTS ONLY */}
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Real API Cost By Service</h4>
                  {apiCostsData && apiCostsData.by_service && apiCostsData.by_service.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {apiCostsData.by_service.slice(0, 6).map((service, index) => (
                        <div key={index} className="p-4 bg-slate-50 rounded-lg border">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div className="font-bold text-slate-900">{service.service}</div>
                              <div className="text-xs text-slate-600">{service.call_count} API calls</div>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Total Cost:</span>
                              <span className="font-bold text-red-600">${service.total_cost.toFixed(4)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Cost per Call:</span>
                              <span className="font-bold text-red-600">${(service.total_cost / service.call_count).toFixed(4)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t">
                              <span className="text-slate-600">API Calls:</span>
                              <span className="font-bold">{service.call_count}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <p className="text-slate-500">No API cost data available yet</p>
                      <p className="text-xs text-slate-400 mt-1">Data will appear once services are used</p>
                    </div>
                  )}
                </div>

                {/* Real Data Summary */}
                <div className="p-4 bg-gradient-to-r from-blue-50 to-slate-50 border-l-4 border-blue-500 rounded-lg">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-2xl">📊</span>
                    <div>
                      <div className="font-bold text-blue-900 mb-1">Cost Structure Overview</div>
                      <div className="space-y-2 text-sm text-blue-800">
                        {apiCostsData && apiCostsData.summary ? (
                          <>
                            <p>• Total API Costs: ${(apiCostsData.summary.total_cost || 0).toFixed(2)} ({apiCostsData.summary.total_revenue > 0 ? ((apiCostsData.summary.total_cost / apiCostsData.summary.total_revenue) * 100).toFixed(1) : 0}% of revenue)</p>
                            <p>• Merchant Processing Fees (est.): ${((apiCostsData.summary.total_revenue || 0) * 0.029 + (apiCostsData.summary.transaction_count || 0) * 0.30).toFixed(2)}</p>
                            <p>• Net Profit Margin: {apiCostsData.summary.total_revenue > 0 ? (((apiCostsData.summary.total_revenue - apiCostsData.summary.total_cost - (apiCostsData.summary.total_revenue * 0.029 + apiCostsData.summary.transaction_count * 0.30)) / apiCostsData.summary.total_revenue) * 100).toFixed(1) : 0}%</p>
                            <p className="text-xs text-blue-600 mt-2">Note: Merchant fees are industry-standard estimates (2.9% + $0.30/txn)</p>
                          </>
                        ) : (
                          <p>No cost data available yet - data will appear once services are used</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ========== DETAILED SERVICE METRICS (Collapsible) ========== */}
            <details className="mb-8">
              <summary className="cursor-pointer bg-slate-100 p-4 rounded-lg hover:bg-slate-200 transition-colors">
                <span className="font-semibold text-slate-900">📋 View Detailed Service Breakdown by Workflow</span>
              </summary>
              
            {/* Service Metrics by Workflow */}
            <div className="space-y-8 mt-6">
              {/* 1. Writing & Ideation Workflow */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">📝 Writing & Ideation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">BCTG Generations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-600">
                        {analytics.service_metrics.bctg.total_generations}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics.bctg.period_generations} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">BOCP Outlines</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-indigo-600">
                        {analytics.service_metrics?.bocp?.total_outlines || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.bocp?.period_outlines || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">CCG Character Casts</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-cyan-600">
                        {analytics.service_metrics?.ccg?.total_casts || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.ccg?.period_casts || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              {/* 2. Editing & Enhancement Workflow */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">✏️ Editing & Enhancement</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">ABG Biographies</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-emerald-600">
                        {analytics.service_metrics?.abg?.total_biographies || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.abg?.period_biographies || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">PDE Analyses</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-purple-600">
                        {analytics.service_metrics?.pde?.total_analyses || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.pde?.period_analyses || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">BVADM Analyses</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-purple-600">
                        {analytics.service_metrics?.bvadm?.total_analyses || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.bvadm?.period_analyses || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              {/* 3. Design & Production Workflow */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">🎨 Design & Production</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">ERMT Conversions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-rose-600">
                        {analytics.service_metrics?.ermt?.total_conversions || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.ermt?.period_conversions || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">PMPB PDF Merges</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-fuchsia-600">
                        {analytics.service_metrics?.pmpb?.total_merges || analytics.service_metrics?.pmpb?.total_plans || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.pmpb?.period_merges || analytics.service_metrics?.pmpb?.period_plans || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">AKSW Keywords</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-purple-600">
                        {analytics.service_metrics?.aksw?.total_keywords || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.aksw?.period_keywords || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              {/* 4. Market Research & Analysis Workflow */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">📊 Market Research & Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">CCAT Analyses</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-teal-600">
                        {analytics.service_metrics?.ccat?.total_analyses || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.ccat?.period_analyses || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">BREC Reviews</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-cyan-600">
                        {analytics.service_metrics?.brec?.total_reviews || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.brec?.period_reviews || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">MMG Reports</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-600">
                        {analytics.service_metrics?.mmg?.total_reports || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.mmg?.period_reports || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              {/* 5. Marketing & Promotion Workflow */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">📈 Marketing & Promotion</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">KSG Keynote Speeches</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-amber-600">
                        {analytics.service_metrics?.ksg?.total_speeches || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.ksg?.period_speeches || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">MMKG Media Kits</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        {analytics.service_metrics?.mmkg?.total_kits || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {analytics.service_metrics?.mmkg?.period_kits || 0} in selected period
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="opacity-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">SMMK Social Media Kits</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-600">
                        {analytics.service_metrics?.smmk?.total_kits || 0}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Coming Soon - Placeholder
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* 6. Free Tools - Lead Generation */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">🎁 Free Tools (Lead Generation)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-2 border-blue-200 bg-blue-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      📚 BCTG - Free Generations
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">FREE</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {analytics.service_metrics?.bctg_free?.total_generations || 0}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {analytics.service_metrics?.bctg_free?.period_generations || 0} in selected period
                    </p>
                    <p className="text-xs text-blue-600 mt-2 font-medium">Free book concept generations</p>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-violet-200 bg-violet-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      📊 IBG - ISBN Barcode Generator
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">FREE</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-violet-600">
                      {analytics.service_metrics?.ibg?.total_generations || 0}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {analytics.service_metrics?.ibg?.period_generations || 0} in selected period
                    </p>
                    <p className="text-xs text-violet-600 mt-2 font-medium">Lead generation tool for authors</p>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-cyan-200 bg-cyan-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      📱 QRG - QR Code Generator
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">FREE</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-cyan-600">
                      {analytics.service_metrics?.qrg?.total_generations || 0}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {analytics.service_metrics?.qrg?.period_generations || 0} in selected period
                    </p>
                    <p className="text-xs text-cyan-600 mt-2 font-medium">Lead generation for marketing needs</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Role Breakdown */}
            {analytics.user_metrics?.role_breakdown && (
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>User Role Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(analytics.user_metrics.role_breakdown).map(([role, count]) => (
                    <div key={role} className="text-center p-4 bg-slate-50 rounded-lg">
                      <div className="text-2xl font-bold text-slate-900">{count}</div>
                      <div className="text-sm text-slate-600 capitalize">{role ? role.replace('_', ' ') : 'Unknown'}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            )}
            </details>

            {/* Signup Trends */}
            {analytics.signup_trends && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Signup Trends (Last 4 Weeks)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics.signup_trends.map((week, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                        <span className="font-medium">{week.week}</span>
                        <span className="text-lg font-bold text-blue-600">{week.signups} signups</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Geographic Distribution */}
            {analytics.geographic_distribution && analytics.geographic_distribution.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Geographic Distribution (Top States)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics.geographic_distribution.map((location, index) => (
                      <div key={index} className="flex justify-between items-center p-2 border-b">
                        <span className="font-medium">{location.state}</span>
                        <span className="text-blue-600">{location.users} users</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* DANGER ZONE - Database Reset */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-600">⚠️ DANGER ZONE</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 border border-red-200 bg-red-50 rounded">
                    <h3 className="font-medium text-red-800 mb-2">Master Reset</h3>
                    <p className="text-sm text-red-600 mb-4">
                      This will permanently delete ALL user data except admin accounts. 
                      This action cannot be undone.
                    </p>
                    <Button 
                      variant="destructive"
                      onClick={masterReset}
                    >
                      Master Reset All Data
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Onboarding Analytics Tab */}
        {activeTab === "onboarding" && onboardingData && (
          <div className="space-y-6">
            {/* Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle>Onboarding Survey Analytics</CardTitle>
                <p className="text-sm text-gray-500 mt-2">
                  Insights from {onboardingData.total_users} users who completed onboarding
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600">{onboardingData.total_users}</div>
                    <div className="text-sm text-gray-600 mt-1">Total Onboarded Users</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-3xl font-bold text-green-600">{onboardingData.email_marketing.opt_in_rate}%</div>
                    <div className="text-sm text-gray-600 mt-1">Email Opt-In Rate</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-3xl font-bold text-purple-600">{onboardingData.email_marketing.opted_in}</div>
                    <div className="text-sm text-gray-600 mt-1">Opted-In for Marketing</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Roles */}
              <Card>
                <CardHeader>
                  <CardTitle>Primary Roles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.primary_roles?.map((role, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">{role._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{role.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Main Goals */}
              <Card>
                <CardHeader>
                  <CardTitle>Main Goals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.main_goals?.slice(0, 5).map((goal, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{goal._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{goal.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Popular Genres */}
              <Card>
                <CardHeader>
                  <CardTitle>Popular Genres</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.popular_genres?.map((genre, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{genre._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{genre.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Experience Levels */}
              <Card>
                <CardHeader>
                  <CardTitle>Experience Levels</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.experience_levels?.map((exp, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">{exp._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{exp.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Publishing Platforms */}
              <Card>
                <CardHeader>
                  <CardTitle>Publishing Platforms</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.publishing_platforms?.map((platform, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{platform._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{platform.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Main Challenges */}
              <Card>
                <CardHeader>
                  <CardTitle>Main Challenges</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.main_challenges?.map((challenge, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{challenge._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{challenge.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Project Stages */}
              <Card>
                <CardHeader>
                  <CardTitle>Project Stages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.project_stages?.map((stage, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{stage._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{stage.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* AI Usage Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle>AI Usage Preferences</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.ai_usage_preferences?.map((pref, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{pref._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{pref.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Target Audiences */}
              <Card>
                <CardHeader>
                  <CardTitle>Target Audiences</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.target_audiences?.map((audience, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{audience._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{audience.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Marketing Importance */}
              <Card>
                <CardHeader>
                  <CardTitle>Marketing Importance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {onboardingData.marketing_importance?.map((importance, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 truncate">{importance._id || 'N/A'}</span>
                        <span className="text-sm font-semibold text-gray-900">{importance.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-6">
            {/* Search and Actions */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>User Management</CardTitle>
                  <Button 
                    onClick={() => setShowCreateUserModal(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    ➕ Create User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-4 mb-4">
                  <Input
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleSearch}>Search</Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setUserSearch("");
                      setCurrentPage(1);
                      loadUsers(1, "");
                    }}
                  >
                    Clear Search
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">User</th>
                        <th className="text-left p-4 font-medium text-slate-600">Role</th>
                        <th className="text-left p-4 font-medium text-slate-600">Subscription</th>
                        <th className="text-left p-4 font-medium text-slate-600">EULA</th>
                        <th className="text-left p-4 font-medium text-slate-600">State</th>
                        <th className="text-left p-4 font-medium text-slate-600">Last Active</th>
                        <th className="text-left p-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((userData) => (
                        <tr key={userData.id} className="border-b hover:bg-slate-50">
                          <td className="p-4">
                            <div>
                              <div className="font-medium text-slate-900">
                                {userData.first_name} {userData.last_name}
                              </div>
                              <div className="text-sm text-slate-500">{userData.email}</div>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={getRoleBadgeColor(userData.role)}>
                              {userData.role ? userData.role.replace('_', ' ') : 'N/A'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <span className="text-sm">{userData.subscription_status}</span>
                          </td>
                          <td className="p-4">
                            {userData.eula_accepted ? (
                              <Badge 
                                className="bg-green-500 text-white cursor-pointer hover:bg-green-600 transition-colors"
                                onClick={() => {
                                  setEulaUser(userData);
                                  setShowEulaModal(true);
                                }}
                              >
                                <span className="text-xs">✓ Accepted</span>
                              </Badge>
                            ) : (
                              <Badge 
                                className="bg-red-500 text-white cursor-pointer hover:bg-red-600 transition-colors"
                                onClick={() => {
                                  setEulaUser(userData);
                                  setShowEulaModal(true);
                                }}
                              >
                                <span className="text-xs">✗ Pending</span>
                              </Badge>
                            )}
                          </td>
                          <td className="p-4">
                            <span className="text-sm">{userData.state || "Not set"}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm">
                              {userData.last_active ? formatTimestamp(userData.last_active) : "Never"}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => editUser(userData)}
                              >
                                ✏️ Edit
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => viewUserTransactions(userData)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                💳 Transactions
                              </Button>
                              {userData.role !== "admin" && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => designateAdmin(userData.id)}
                                  >
                                    Make Admin
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => archiveUser(userData.id)}
                                  >
                                    Archive
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => deleteUser(userData.id)}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center p-4 border-t">
                    <div className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={currentPage === 1}
                        onClick={() => {
                          const newPage = currentPage - 1;
                          setCurrentPage(newPage);
                          loadUsers(newPage, userSearch);
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={currentPage === totalPages}
                        onClick={() => {
                          const newPage = currentPage + 1;
                          setCurrentPage(newPage);
                          loadUsers(newPage, userSearch);
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rewards Management Tab */}
        {activeTab === "rewards" && (
          loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg text-slate-600">Loading rewards data...</div>
            </div>
          ) : rewardsData ? (
          <div className="space-y-6">
            {/* Rewards Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Points Issued</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {rewardsData.overview.total_points_issued.toLocaleString()}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">All time</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Points Redeemed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {rewardsData.overview.total_points_redeemed.toLocaleString()}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">${(rewardsData.overview.total_points_redeemed * 0.01).toFixed(2)} value</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Outstanding Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    ${rewardsData.overview.total_dollar_value}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Unredeemed points</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Successful Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600">
                    {rewardsData.overview.successful_referrals}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Users referred</p>
                </CardContent>
              </Card>
            </div>

            {/* Top Earners */}
            {rewardsData.top_earners && rewardsData.top_earners.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>🏆 Top Point Earners</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {rewardsData.top_earners.map((earner, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{earner.first_name} {earner.last_name}</span>
                        <span className="text-sm text-slate-500 ml-2">({earner.email})</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">{earner.total_points_earned.toLocaleString()} pts</div>
                        <div className="text-sm text-slate-500">Balance: {earner.points_balance.toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Points Adjustment Tool */}
            <Card>
              <CardHeader>
                <CardTitle>⚙️ Manual Points Adjustment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>User Email</Label>
                    <select
                      className="w-full p-2 border rounded"
                      value={pointsAdjustment.user_id}
                      onChange={(e) => setPointsAdjustment({...pointsAdjustment, user_id: e.target.value})}
                    >
                      <option value="">Select User (Non-Admins Only)</option>
                      {Array.isArray(allNonAdminUsers) && allNonAdminUsers.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.email} ({user.first_name} {user.last_name})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Points (+ or -)</Label>
                    <Input
                      type="number"
                      placeholder="e.g. 100 or -50"
                      value={pointsAdjustment.points}
                      onChange={(e) => setPointsAdjustment({...pointsAdjustment, points: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Reason</Label>
                    <Input
                      placeholder="Adjustment reason"
                      value={pointsAdjustment.reason}
                      onChange={(e) => setPointsAdjustment({...pointsAdjustment, reason: e.target.value})}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={adjustUserPoints} className="w-full">
                      Adjust Points
                    </Button>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> All adjustments are logged and tracked. Use positive numbers to add points, negative to deduct.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Pending Redemptions */}
            {rewardsData.pending_redemptions && rewardsData.pending_redemptions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>💰 Pending Redemption Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {rewardsData.pending_redemptions.map((redemption, index) => (
                      <div key={index} className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{redemption.redemption_type ? redemption.redemption_type.replace('_', ' ').toUpperCase() : 'N/A'}</div>
                            <div className="text-sm text-slate-600">
                              User: {redemption.user_id} • {redemption.points_used} points → ${redemption.credit_amount}
                            </div>
                            <div className="text-xs text-slate-500">
                              Requested: {formatTimestamp(redemption.created_at)}
                            </div>
                          </div>
                          <Badge className="bg-orange-600 text-white">Pending</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg text-slate-600">
                <p>Failed to load rewards data</p>
                <Button onClick={loadRewardsData} className="mt-4">
                  Retry Loading
                </Button>
              </div>
            </div>
          )
        )}

        {/* Activity Summary Tab */}
        {activeTab === "activity_summary" && analytics && (
          <div className="space-y-6">
            {/* Date Filter Controls for Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Activity Summary Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <Label>Date Range</Label>
                    <select 
                      className="w-full p-2 border rounded mt-1"
                      value={summaryDateRange}
                      onChange={(e) => {
                        setSummaryDateRange(e.target.value);
                        if (e.target.value !== "custom") {
                          loadAnalytics(e.target.value);
                        }
                      }}
                    >
                      <option value="7d">Last 7 days</option>
                      <option value="30d">Last 30 days</option>
                      <option value="90d">Last 90 days</option>
                      <option value="1y">Last year</option>
                      <option value="lifetime">All time</option>
                      <option value="custom">Custom range</option>
                    </select>
                  </div>
                  
                  {summaryDateRange === "custom" && (
                    <>
                      <div>
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={summaryStartDate}
                          onChange={(e) => setSummaryStartDate(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={summaryEndDate}
                          onChange={(e) => setSummaryEndDate(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <Button 
                        onClick={() => {
                          if (summaryStartDate && summaryEndDate) {
                            loadAnalytics(null, summaryStartDate, summaryEndDate);
                          }
                        }}
                        disabled={!summaryStartDate || !summaryEndDate}
                      >
                        Apply Filter
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Activity Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Signups Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily User Signups</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end space-x-2 overflow-x-auto">
                    {analytics.activity_metrics.daily_signups.slice(-14).map((day, index) => {
                      const maxCount = Math.max(...analytics.activity_metrics.daily_signups.slice(-14).map(d => d.count));
                      const height = maxCount > 0 ? (day.count / maxCount) * 200 : 5;
                      return (
                        <div key={index} className="flex flex-col items-center min-w-[30px]">
                          <div 
                            className="bg-blue-500 w-6 rounded-t" 
                            style={{ height: `${height}px` }}
                            title={`${day.date}: ${day.count} signups`}
                          ></div>
                          <div className="text-xs mt-2 transform -rotate-45 origin-left">
                            {day.date.split('-')[2]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              
              {/* Daily Logins Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Login Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end space-x-2 overflow-x-auto">
                    {analytics.activity_metrics.daily_logins.slice(-14).map((day, index) => {
                      const maxCount = Math.max(...analytics.activity_metrics.daily_logins.slice(-14).map(d => d.count));
                      const height = maxCount > 0 ? (day.count / maxCount) * 200 : 5;
                      return (
                        <div key={index} className="flex flex-col items-center min-w-[30px]">
                          <div 
                            className="bg-green-500 w-6 rounded-t" 
                            style={{ height: `${height}px` }}
                            title={`${day.date}: ${day.count} logins`}
                          ></div>
                          <div className="text-xs mt-2 transform -rotate-45 origin-left">
                            {day.date.split('-')[2]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              
              {/* Service Usage Chart - BY WORKFLOW */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Service Usage by Workflow (Last 14 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 flex gap-4 text-sm flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded"></div>
                      <span>Writing & Ideation (BCTG, BOCP, CCG)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-purple-500 rounded"></div>
                      <span>Editing & Enhancement (ABG, PDE, BVADM)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-rose-500 rounded"></div>
                      <span>Design & Production (ERMT, PMPB, AKSW)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-teal-500 rounded"></div>
                      <span>Market Research (CCAT, BREC, MMG)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-orange-500 rounded"></div>
                      <span>Marketing & Promotion (KSG, MMKG, SMMK)</span>
                    </div>
                  </div>
                  <div className="h-64 flex items-end space-x-2 overflow-x-auto">
                    {analytics.activity_metrics.daily_bctg_usage && analytics.activity_metrics.daily_bctg_usage.slice(-14).map((day, index) => {
                      // Get data for all services
                      const bctgData = analytics.activity_metrics.daily_bctg_usage.slice(-14);
                      const bocpData = analytics.activity_metrics.daily_bocp_usage ? analytics.activity_metrics.daily_bocp_usage.slice(-14) : [];
                      const ccgData = analytics.activity_metrics.daily_ccg_usage ? analytics.activity_metrics.daily_ccg_usage.slice(-14) : [];
                      const abgData = analytics.activity_metrics.daily_abg_usage ? analytics.activity_metrics.daily_abg_usage.slice(-14) : [];
                      const pdeData = analytics.activity_metrics.daily_pde_usage ? analytics.activity_metrics.daily_pde_usage.slice(-14) : [];
                      const ksgData = analytics.activity_metrics.daily_ksg_usage ? analytics.activity_metrics.daily_ksg_usage.slice(-14) : [];
                      const bvadmData = analytics.activity_metrics.daily_bvadm_usage ? analytics.activity_metrics.daily_bvadm_usage.slice(-14) : [];
                      const ccatData = analytics.activity_metrics.daily_ccat_usage ? analytics.activity_metrics.daily_ccat_usage.slice(-14) : [];
                      const brecData = analytics.activity_metrics.daily_brec_usage ? analytics.activity_metrics.daily_brec_usage.slice(-14) : [];
                      const mmgData = analytics.activity_metrics.daily_mmg_usage ? analytics.activity_metrics.daily_mmg_usage.slice(-14) : [];
                      const pmpbData = analytics.activity_metrics.daily_pmpb_usage ? analytics.activity_metrics.daily_pmpb_usage.slice(-14) : [];
                      const ermtData = analytics.activity_metrics.daily_ermt_usage ? analytics.activity_metrics.daily_ermt_usage.slice(-14) : [];
                      
                      // Group by workflow (aligned with UserDashboard)
                      const writingCount = (bctgData[index]?.count || 0) + (bocpData[index]?.count || 0) + (ccgData[index]?.count || 0);
                      const editingCount = (abgData[index]?.count || 0) + (pdeData[index]?.count || 0) + (bvadmData[index]?.count || 0);
                      const designCount = (ermtData[index]?.count || 0) + (pmpbData[index]?.count || 0);
                      const researchCount = (ccatData[index]?.count || 0) + (brecData[index]?.count || 0) + (mmgData[index]?.count || 0);
                      const marketingCount = (ksgData[index]?.count || 0);
                      
                      const maxCount = Math.max(
                        ...bctgData.slice(-14).map((d, i) => 
                          (d.count || 0) + 
                          (bocpData[i]?.count || 0) + 
                          (ccgData[i]?.count || 0) + 
                          (abgData[i]?.count || 0) +
                          (pdeData[i]?.count || 0) +
                          (bvadmData[i]?.count || 0) + 
                          (ccatData[i]?.count || 0) + 
                          (brecData[i]?.count || 0) + 
                          (mmgData[i]?.count || 0) +
                          (ksgData[i]?.count || 0) +
                          (pmpbData[i]?.count || 0) +
                          (ermtData[i]?.count || 0)
                        ),
                        1
                      );
                      
                      const writingHeight = (writingCount / maxCount) * 200;
                      const editingHeight = (editingCount / maxCount) * 200;
                      const designHeight = (designCount / maxCount) * 200;
                      const researchHeight = (researchCount / maxCount) * 200;
                      const marketingHeight = (marketingCount / maxCount) * 200;

                      return (
                        <div key={index} className="flex-shrink-0 flex flex-col items-center group relative">
                          <div className="h-64 w-20 flex flex-col justify-end">
                            <div className="flex flex-col-reverse gap-1">
                              {writingHeight > 0 && (
                                <div style={{height: `${writingHeight}px`}} className="bg-blue-500 w-full rounded-t hover:bg-blue-600 transition-colors" title={`Writing: ${writingCount}`}></div>
                              )}
                              {editingHeight > 0 && (
                                <div style={{height: `${editingHeight}px`}} className="bg-purple-500 w-full rounded-t hover:bg-purple-600 transition-colors" title={`Editing: ${editingCount}`}></div>
                              )}
                              {designHeight > 0 && (
                                <div style={{height: `${designHeight}px`}} className="bg-rose-500 w-full rounded-t hover:bg-rose-600 transition-colors" title={`Design: ${designCount}`}></div>
                              )}
                              {researchHeight > 0 && (
                                <div style={{height: `${researchHeight}px`}} className="bg-teal-500 w-full rounded-t hover:bg-teal-600 transition-colors" title={`Research: ${researchCount}`}></div>
                              )}
                              {marketingHeight > 0 && (
                                <div style={{height: `${marketingHeight}px`}} className="bg-orange-500 w-full rounded-t hover:bg-orange-600 transition-colors" title={`Marketing: ${marketingCount}`}></div>
                              )}
                            </div>
                          </div>
                          <span className="text-xs mt-2">{day.date.split('-')[1]}/{day.date.split('-')[2]}</span>
                          <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                            <div>Writing: {writingCount}</div>
                            <div>Editing: {editingCount}</div>
                            <div>Design: {designCount}</div>
                            <div>Research: {researchCount}</div>
                            <div>Marketing: {marketingCount}</div>
                            <div className="font-bold">Total: {writingCount + editingCount + designCount + researchCount + marketingCount}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Activity Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                      <span className="font-medium">Total Login Events</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {analytics.activity_metrics.login_events}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                      <span className="font-medium">Avg Daily Signups</span>
                      <span className="text-2xl font-bold text-green-600">
                        {(analytics.activity_metrics.daily_signups.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_signups.length).toFixed(1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                      <span className="font-medium">Avg Daily BCTG Usage</span>
                      <span className="text-2xl font-bold text-purple-600">
                        {analytics.activity_metrics.daily_bctg_usage ? (analytics.activity_metrics.daily_bctg_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_bctg_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                      <span className="font-medium">Avg Daily BVADM Usage</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {analytics.activity_metrics.daily_bvadm_usage ? (analytics.activity_metrics.daily_bvadm_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_bvadm_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded">
                      <span className="font-medium">Avg Daily BOCP Usage</span>
                      <span className="text-2xl font-bold text-emerald-600">
                        {analytics.activity_metrics.daily_bocp_usage ? (analytics.activity_metrics.daily_bocp_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_bocp_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded">
                      <span className="font-medium">Avg Daily MMG Usage</span>
                      <span className="text-2xl font-bold text-orange-600">
                        {analytics.activity_metrics.daily_mmg_usage ? (analytics.activity_metrics.daily_mmg_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_mmg_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-teal-50 rounded">
                      <span className="font-medium">Avg Daily CCAT Usage</span>
                      <span className="text-2xl font-bold text-teal-600">
                        {analytics.activity_metrics.daily_ccat_usage ? (analytics.activity_metrics.daily_ccat_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_ccat_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-cyan-50 rounded">
                      <span className="font-medium">Avg Daily BREC Usage</span>
                      <span className="text-2xl font-bold text-cyan-600">
                        {analytics.activity_metrics.daily_brec_usage ? (analytics.activity_metrics.daily_brec_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_brec_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-indigo-50 rounded">
                      <span className="font-medium">Avg Daily CCG Usage</span>
                      <span className="text-2xl font-bold text-indigo-600">
                        {analytics.activity_metrics.daily_ccg_usage ? (analytics.activity_metrics.daily_ccg_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_ccg_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-violet-50 rounded">
                      <span className="font-medium">Avg Daily ABG Usage</span>
                      <span className="text-2xl font-bold text-violet-600">
                        {analytics.activity_metrics.daily_abg_usage ? (analytics.activity_metrics.daily_abg_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_abg_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded">
                      <span className="font-medium">Avg Daily KSG Usage</span>
                      <span className="text-2xl font-bold text-amber-600">
                        {analytics.activity_metrics.daily_ksg_usage ? (analytics.activity_metrics.daily_ksg_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_ksg_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                      <span className="font-medium">Avg Daily MMKG Usage</span>
                      <span className="text-2xl font-bold text-green-600">
                        {analytics.activity_metrics.daily_mmkg_usage ? (analytics.activity_metrics.daily_mmkg_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_mmkg_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                      <span className="font-medium">Avg Daily PDE Usage</span>
                      <span className="text-2xl font-bold text-purple-600">
                        {analytics.activity_metrics.daily_pde_usage ? (analytics.activity_metrics.daily_pde_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_pde_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-indigo-50 rounded">
                      <span className="font-medium">Avg Daily ERMT Usage</span>
                      <span className="text-2xl font-bold text-indigo-600">
                        {analytics.activity_metrics.daily_ermt_usage ? (analytics.activity_metrics.daily_ermt_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_ermt_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-fuchsia-50 rounded">
                      <span className="font-medium">Avg Daily PMPB Usage</span>
                      <span className="text-2xl font-bold text-fuchsia-600">
                        {analytics.activity_metrics.daily_pmpb_usage ? (analytics.activity_metrics.daily_pmpb_usage.reduce((sum, day) => sum + day.count, 0) / analytics.activity_metrics.daily_pmpb_usage.length).toFixed(1) : '0.0'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Activity Logs Tab */}
        {activeTab === "activity_logs" && (
          <div className="space-y-6">
            {/* Search and Filter Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Activity Logs Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label>Search User</Label>
                    <Input
                      placeholder="Name or email..."
                      value={logSearchUser}
                      onChange={(e) => setLogSearchUser(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Activity Type</Label>
                    <select 
                      className="w-full p-2 border rounded"
                      value={logActivityType}
                      onChange={(e) => setLogActivityType(e.target.value)}
                    >
                      <option value="">All types</option>
                      <option value="login">Login</option>
                      <option value="service_usage">Service Usage</option>
                      <option value="admin_action">Admin Action</option>
                      <option value="error">Errors</option>
                    </select>
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={logStartDate}
                      onChange={(e) => setLogStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={logEndDate}
                      onChange={(e) => setLogEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button onClick={() => loadActivityLogs(1)}>
                    Apply Filters
                  </Button>
                  <Button 
                    variant="outline" 
                    className="ml-2"
                    onClick={() => {
                      setLogSearchUser("");
                      setLogActivityType("");
                      setLogStartDate("");
                      setLogEndDate("");
                      loadActivityLogs(1);
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Activity Logs List */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity Logs</CardTitle>
              </CardHeader>
              <CardContent>
              <div className="space-y-2">
                {activityLogs.map((log) => (
                  <div key={log.id} className="flex justify-between items-center p-3 border rounded">
                    <div>
                      <div className="font-medium">
                        {log.activity_type ? log.activity_type.replace('_', ' ').toUpperCase() : 'N/A'}
                        {log.service_name && ` - ${log.service_name.toUpperCase()}`}
                      </div>
                      <div className="text-sm text-slate-600">
                        User: {log.user_info ? `${log.user_info.name} (${log.user_info.email})` : "System"} | {log.success ? "✅ Success" : "❌ Failed"}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">
                            {JSON.stringify(log.details).substring(0, 50)}...
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-slate-500">
                      {log.timestamp_display || formatTimestamp(log.timestamp)}
                    </div>
                  </div>
                ))}
                {activityLogs.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No activity logs found matching the current filters.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Revenue Analytics Tab */}
        {activeTab === "revenue" && revenueData && (
          <div className="space-y-6">
            {/* Revenue Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue Analytics Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="revenueDateRange">Date Range</Label>
                    <select 
                      id="revenueDateRange"
                      value={revenueDateRange}
                      onChange={(e) => setRevenueDateRange(e.target.value)}
                      className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                      <option value="90d">Last 90 Days</option>
                      <option value="1y">Last Year</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                  
                  {revenueDateRange === "custom" && (
                    <>
                      <div>
                        <Label htmlFor="revenueStartDate">Start Date</Label>
                        <Input
                          id="revenueStartDate"
                          type="date"
                          value={revenueStartDate}
                          onChange={(e) => setRevenueStartDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="revenueEndDate">End Date</Label>
                        <Input
                          id="revenueEndDate"
                          type="date"
                          value={revenueEndDate}
                          onChange={(e) => setRevenueEndDate(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
                
                <div className="flex justify-end mt-4">
                  <Button onClick={loadRevenueData} disabled={loading}>
                    {loading ? "Loading..." : "Update Analytics"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Revenue Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    ${revenueData.totals?.revenue?.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {revenueData.totals?.revenue_growth > 0 ? '↗️' : revenueData.totals?.revenue_growth < 0 ? '↘️' : '➡️'} 
                    {Math.abs(revenueData.totals?.revenue_growth || 0).toFixed(1)}% vs previous period
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {revenueData.totals?.transactions || 0}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Completed payments
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Avg Transaction</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    ${revenueData.totals?.avg_transaction_value?.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Per transaction
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Period</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-bold text-gray-700">
                    {new Date(revenueData.period?.start).toLocaleDateString('en-US', { timeZone: 'America/Denver' })} - {new Date(revenueData.period?.end).toLocaleDateString('en-US', { timeZone: 'America/Denver' })}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {revenueData.period?.range.toUpperCase()}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue by Service */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue by Service</CardTitle>
              </CardHeader>
              <CardContent>
                {revenueData.by_service && revenueData.by_service.length > 0 ? (
                  <div className="space-y-4">
                    {revenueData.by_service.map((service) => (
                      <div key={service._id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl">
                            {service._id === 'BCTG' ? '📚' : 
                             service._id === 'BVADM' ? '📊' : 
                             service._id === 'CCAT' ? '📊' :
                             service._id === 'BREC' ? '📚' :
                             service._id === 'BOCP' ? '📖' : 
                             service._id === 'MMG' ? '📈' : 
                             service._id === 'ABG' ? '📝' :
                             service._id === 'CCG' ? '🎭' :
                             service._id === 'AKSW' ? '🔍' :
                             service._id === 'KSG' ? '🎤' :
                             service._id === 'MMKG' ? '📣' :
                             service._id === 'SMMK' ? '📱' :
                             service._id === 'IBG' ? '📊' :
                             service._id === 'QRG' ? '📱' : '💼'}
                          </div>
                          <div>
                            <div className="font-semibold">
                              {service._id === 'BCTG' ? 'BCTG - Book Concept & Title Generator' : 
                               service._id === 'BVADM' ? 'BVADM - Book Viability & Development Model' : 
                               service._id === 'CCAT' ? 'CCAT - Competitor & Comparative Analysis Tool' :
                               service._id === 'BREC' ? 'BREC - Book Review & Endorsement Creator' :
                               service._id === 'BOCP' ? 'BOCP - Book Outline & Chapter Planner' :
                               service._id === 'MMG' ? 'MMG - Manuscript Metadata Generator' :
                               service._id === 'ABG' ? 'ABG - Author Biography Generator' :
                               service._id === 'CCG' ? 'CCG - Character & Cast Generator' :
                               service._id === 'AKSW' ? 'AKSW - Amazon Keyword Search Wizard' :
                               service._id === 'KSG' ? 'KSG - Keynote Speech Generator' :
                               service._id === 'MMKG' ? 'MMKG - Manuscript Media Kit Generator' :
                               service._id === 'SMMK' ? 'SMMK - Social Media Marketing Kit' :
                               service._id === 'IBG' ? 'IBG - ISBN Barcode Generator (FREE)' :
                               service._id === 'QRG' ? 'QRG - QR Code Generator (FREE)' :
                               service._id.toUpperCase()}
                            </div>
                            <div className="text-sm text-gray-500">
                              {service.transaction_count} transactions • Avg: ${service.avg_transaction?.toFixed(2)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            ${service.total_revenue?.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {((service.total_revenue / revenueData.totals.revenue) * 100).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <p className="text-lg mb-2">No service revenue data yet</p>
                    <p className="text-sm">Revenue will appear here once users make paid transactions for BCTG, BVADM, CCAT, BREC, BOCP, MMG, CCG, AKSW, KSG, MMKG, or SMMK services.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue by User Role */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue by User Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {revenueData.by_user_role?.map((role) => (
                    <div key={role._id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">
                          {role._id === 'a_la_carte' ? '💳' : 
                           role._id === 'subscriber' ? '⭐' : 
                           role._id === 'student' ? '🎓' : 
                           role._id === 'free_user' ? '🆓' : '👤'}
                        </div>
                        <div>
                          <div className="font-semibold capitalize">
                            {role._id ? role._id.replace('_', ' ') : 'Unknown'} Users
                          </div>
                          <div className="text-sm text-gray-500">
                            {role.unique_users_count} users • {role.transactions} transactions
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">
                          ${role.revenue?.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">
                          ${(role.revenue / role.unique_users_count).toFixed(2)} per user
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Transaction Management Tab */}
        {activeTab === "transactions" && (
          <div className="space-y-6">
            {/* Filters Card */}
            <Card>
              <CardHeader>
                <CardTitle>Transaction Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Search User/Email</Label>
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={transactionSearch}
                      onChange={(e) => setTransactionSearch(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Service Type</Label>
                    <select
                      value={transactionServiceFilter}
                      onChange={(e) => setTransactionServiceFilter(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="ALL">All Services</option>
                      <option value="BCTG">BCTG</option>
                      <option value="BVADM">BVADM</option>
                      <option value="CCAT">CCAT</option>
                      <option value="BREC">BREC</option>
                      <option value="BOCP">BOCP</option>
                      <option value="MMG">MMG</option>
                      <option value="CCG">CCG</option>
                      <option value="AKSW">AKSW</option>
                      <option value="KSG">KSG</option>
                      <option value="MMKG">MMKG</option>
                      <option value="SMMK">SMMK</option>
                      <option value="IBG">IBG (Free)</option>
                      <option value="QRG">QRG (Free)</option>
                      <option value="Monthly">Monthly Subscription</option>
                      <option value="Annual">Annual Subscription</option>
                    </select>
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={transactionDateStart}
                      onChange={(e) => setTransactionDateStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={transactionDateEnd}
                      onChange={(e) => setTransactionDateEnd(e.target.value)}
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => {
                    setTransactionSearch("");
                    setTransactionServiceFilter("ALL");
                    setTransactionDateStart("");
                    setTransactionDateEnd("");
                  }}
                  className="mt-4"
                  variant="outline"
                >
                  Clear Filters
                </Button>
              </CardContent>
            </Card>

            {/* Transactions List Card */}
            <Card>
              <CardHeader>
                <CardTitle>All Transactions ({transactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading transactions...</div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No transactions found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="p-3 text-left">Date</th>
                          <th className="p-3 text-left">User</th>
                          <th className="p-3 text-left">Service</th>
                          <th className="p-3 text-right">Amount</th>
                          <th className="p-3 text-left">Status</th>
                          <th className="p-3 text-left">Transaction ID</th>
                          <th className="p-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction) => (
                          <tr key={transaction.id} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              {transaction.created_at_display || formatTimestamp(transaction.created_at)}
                            </td>
                            <td className="p-3">
                              <div className="text-sm">
                                <div className="font-medium">{transaction.user_name}</div>
                                <div className="text-slate-500">{transaction.user_email}</div>
                              </div>
                            </td>
                            <td className="p-3">{transaction.service}</td>
                            <td className="p-3 text-right font-semibold">
                              ${(transaction.amount || 0).toFixed(2)}
                            </td>
                            <td className="p-3">
                              <Badge variant={transaction.payment_status === 'refunded' ? 'destructive' : 'success'}>
                                {transaction.payment_status || 'completed'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="text-xs font-mono text-slate-600">
                                <div>{transaction.id.substring(0, 8)}...</div>
                                {transaction.stripe_payment_intent_id && (
                                  <div className="text-blue-600 mt-1">
                                    🔗 {transaction.stripe_payment_intent_id.substring(0, 15)}...
                                  </div>
                                )}
                                {!transaction.stripe_payment_intent_id && transaction.simulated && (
                                  <div className="text-orange-500 mt-1">
                                    ⚠️ Simulated
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2 justify-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleResendReceipt(transaction)}
                                  title="Re-send Receipt"
                                >
                                  📧
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePrintTransaction(transaction)}
                                  title="Print/Download"
                                >
                                  🖨️
                                </Button>
                                {transaction.stripe_payment_intent_id && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedStripeTransaction(transaction);
                                      setShowStripeModal(true);
                                    }}
                                    title="View Stripe Payment Details"
                                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                                  >
                                    💳
                                  </Button>
                                )}
                                {transaction.payment_status !== 'refunded' && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                      setSelectedTransaction(transaction);
                                      setShowRefundModal(true);
                                    }}
                                    title="Refund Transaction"
                                  >
                                    💰
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      </div>

      {/* Enhanced User Edit Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header with Logo */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-8 w-auto"
                  />
                  <div>
                    <h3 className="text-lg font-medium text-slate-900">Edit User Profile</h3>
                    <p className="text-sm text-slate-600">Email: {selectedUser.email}</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowUserModal(false)}
                >
                  ✕
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-slate-900 mb-3">Personal Information</h4>
                  
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={selectedUser.first_name || ""}
                      onChange={(e) => setSelectedUser({...selectedUser, first_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={selectedUser.last_name || ""}
                      onChange={(e) => setSelectedUser({...selectedUser, last_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      value={selectedUser.phone || ""}
                      onChange={(e) => setSelectedUser({...selectedUser, phone: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Alternate Email</Label>
                    <Input
                      type="email"
                      value={selectedUser.alt_email || ""}
                      onChange={(e) => setSelectedUser({...selectedUser, alt_email: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-slate-900 mb-3">Address Information</h4>
                  
                  <div>
                    <Label>Address</Label>
                    <Input
                      value={selectedUser.address || ""}
                      onChange={(e) => setSelectedUser({...selectedUser, address: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>City</Label>
                    <Input
                      value={selectedUser.city || ""}
                      onChange={(e) => setSelectedUser({...selectedUser, city: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>State</Label>
                      <select 
                        className="w-full p-2 border rounded mt-1"
                        value={selectedUser.state || ""}
                        onChange={(e) => setSelectedUser({...selectedUser, state: e.target.value})}
                      >
                        <option value="">Select State</option>
                        <option value="AL">Alabama</option>
                        <option value="AK">Alaska</option>
                        <option value="AZ">Arizona</option>
                        <option value="AR">Arkansas</option>
                        <option value="CA">California</option>
                        <option value="CO">Colorado</option>
                        <option value="CT">Connecticut</option>
                        <option value="DE">Delaware</option>
                        <option value="FL">Florida</option>
                        <option value="GA">Georgia</option>
                        <option value="HI">Hawaii</option>
                        <option value="ID">Idaho</option>
                        <option value="IL">Illinois</option>
                        <option value="IN">Indiana</option>
                        <option value="IA">Iowa</option>
                        <option value="KS">Kansas</option>
                        <option value="KY">Kentucky</option>
                        <option value="LA">Louisiana</option>
                        <option value="ME">Maine</option>
                        <option value="MD">Maryland</option>
                        <option value="MA">Massachusetts</option>
                        <option value="MI">Michigan</option>
                        <option value="MN">Minnesota</option>
                        <option value="MS">Mississippi</option>
                        <option value="MO">Missouri</option>
                        <option value="MT">Montana</option>
                        <option value="NE">Nebraska</option>
                        <option value="NV">Nevada</option>
                        <option value="NH">New Hampshire</option>
                        <option value="NJ">New Jersey</option>
                        <option value="NM">New Mexico</option>
                        <option value="NY">New York</option>
                        <option value="NC">North Carolina</option>
                        <option value="ND">North Dakota</option>
                        <option value="OH">Ohio</option>
                        <option value="OK">Oklahoma</option>
                        <option value="OR">Oregon</option>
                        <option value="PA">Pennsylvania</option>
                        <option value="RI">Rhode Island</option>
                        <option value="SC">South Carolina</option>
                        <option value="SD">South Dakota</option>
                        <option value="TN">Tennessee</option>
                        <option value="TX">Texas</option>
                        <option value="UT">Utah</option>
                        <option value="VT">Vermont</option>
                        <option value="VA">Virginia</option>
                        <option value="WA">Washington</option>
                        <option value="WV">West Virginia</option>
                        <option value="WI">Wisconsin</option>
                        <option value="WY">Wyoming</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label>Zip Code</Label>
                      <Input
                        value={selectedUser.zip_code || ""}
                        onChange={(e) => setSelectedUser({...selectedUser, zip_code: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Settings */}
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Account Settings</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Role</Label>
                    <select 
                      className="w-full p-2 border rounded mt-1"
                      value={selectedUser.role}
                      onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                    >
                      <option value="a_la_carte">A La Carte (Default)</option>
                      <option value="free_user">Free User</option>
                      <option value="subscriber">Subscriber</option>
                      <option value="student">Student</option>
                      <option value="beta_tester">Beta Tester</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label>Subscription Status</Label>
                    <select 
                      className="w-full p-2 border rounded mt-1"
                      value={selectedUser.subscription_status}
                      onChange={(e) => setSelectedUser({...selectedUser, subscription_status: e.target.value})}
                    >
                      <option value="free">Free</option>
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                      <option value="student_spring">Student - Spring</option>
                      <option value="student_fall">Student - Fall</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label>Service Access</Label>
                    <div className="mt-1 p-2 border rounded">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedUser.service_enabled !== false}
                          onChange={(e) => setSelectedUser({...selectedUser, service_enabled: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">{selectedUser.service_enabled !== false ? "Enabled" : "Blocked"}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* EULA Acceptance Status - Phase 1 & 2 */}
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-md font-semibold text-slate-900 mb-4">EULA Acceptance</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">Accepted</Label>
                    <div className="mt-1">
                      {selectedUser.eula_accepted ? (
                        <Badge className="bg-green-500 text-white">✓ Yes</Badge>
                      ) : (
                        <Badge className="bg-red-500 text-white">✗ No</Badge>
                      )}
                    </div>
                  </div>
                  
                  {selectedUser.eula_accepted && (
                    <>
                      <div>
                        <Label className="text-sm text-slate-600">Accepted Name</Label>
                        <p className="mt-1 text-sm text-slate-900">{selectedUser.eula_accepted_name || "N/A"}</p>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-slate-600">Accepted Email</Label>
                        <p className="mt-1 text-sm text-slate-900">{selectedUser.eula_accepted_email || "N/A"}</p>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-slate-600">Accepted Date</Label>
                        <p className="mt-1 text-sm text-slate-900">
                          {selectedUser.eula_accepted_at ? formatTimestamp(selectedUser.eula_accepted_at) : "N/A"}
                        </p>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-slate-600">EULA Version</Label>
                        <p className="mt-1 text-sm text-slate-900">{selectedUser.eula_version || "N/A"}</p>
                      </div>
                      
                      {selectedUser.eula_accepted_ip && (
                        <div>
                          <Label className="text-sm text-slate-600">IP Address</Label>
                          <p className="mt-1 text-sm text-slate-900 font-mono">{selectedUser.eula_accepted_ip}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Phase 2: Email Copy Status */}
                {selectedUser.eula_accepted && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <h5 className="text-sm font-semibold text-slate-700 mb-3">EULA Email Copy</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-slate-600">Offered At</Label>
                        <p className="mt-1 text-sm text-slate-900">
                          {selectedUser.eula_email_copy_offered_at ? formatTimestamp(selectedUser.eula_email_copy_offered_at) : "Not offered"}
                        </p>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-slate-600">Requested</Label>
                        <div className="mt-1">
                          {selectedUser.eula_email_copy_requested ? (
                            <Badge className="bg-blue-500 text-white">✓ Yes</Badge>
                          ) : (
                            <Badge className="bg-slate-400 text-white">✗ No</Badge>
                          )}
                        </div>
                      </div>
                      
                      {selectedUser.eula_email_copy_requested && (
                        <>
                          <div>
                            <Label className="text-sm text-slate-600">Sent At</Label>
                            <p className="mt-1 text-sm text-slate-900">
                              {selectedUser.eula_email_copy_sent_at ? formatTimestamp(selectedUser.eula_email_copy_sent_at) : "Not sent"}
                            </p>
                          </div>
                          
                          {selectedUser.eula_email_copy_last_error && (
                            <div className="md:col-span-2">
                              <Label className="text-sm text-slate-600">Last Error</Label>
                              <p className="mt-1 text-sm text-red-600 font-mono text-xs">
                                {selectedUser.eula_email_copy_last_error}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-between mt-8 pt-4 border-t">
                <div className="text-xs text-slate-500">
                  Changes will be recorded for historical reference
                </div>
                <div className="flex space-x-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowUserModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => updateUser(selectedUser.id, {
                      first_name: selectedUser.first_name,
                      last_name: selectedUser.last_name,
                      phone: selectedUser.phone,
                      alt_email: selectedUser.alt_email,
                      address: selectedUser.address,
                      city: selectedUser.city,
                      state: selectedUser.state,
                      zip_code: selectedUser.zip_code,
                      role: selectedUser.role,
                      subscription_status: selectedUser.subscription_status,
                      service_enabled: selectedUser.service_enabled
                    })}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EULA Details Modal */}
      {showEulaModal && eulaUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">EULA Acceptance Details</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {eulaUser.first_name} {eulaUser.last_name} ({eulaUser.email})
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowEulaModal(false);
                    setEulaUser(null);
                  }}
                >
                  ✕
                </Button>
              </div>

              {/* EULA Status Overview */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Acceptance Status</h4>
                    <p className="text-sm text-slate-600">
                      {eulaUser.eula_accepted 
                        ? `Accepted on ${eulaUser.eula_accepted_at ? formatTimestamp(eulaUser.eula_accepted_at) : 'N/A'}`
                        : 'User has not accepted the EULA'}
                    </p>
                  </div>
                  <div>
                    {eulaUser.eula_accepted ? (
                      <Badge className="bg-green-500 text-white text-lg px-4 py-2">
                        ✓ Accepted
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500 text-white text-lg px-4 py-2">
                        ✗ Pending
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Detailed Information */}
              {eulaUser.eula_accepted ? (
                <div className="space-y-6">
                  {/* Acceptance Details */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 text-lg">Acceptance Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">Accepted Name</Label>
                        <p className="mt-1 text-sm text-slate-900 font-medium">
                          {eulaUser.eula_accepted_name || "N/A"}
                        </p>
                      </div>

                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">Accepted Email</Label>
                        <p className="mt-1 text-sm text-slate-900 font-medium">
                          {eulaUser.eula_accepted_email || "N/A"}
                        </p>
                      </div>

                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">Acceptance Date</Label>
                        <p className="mt-1 text-sm text-slate-900 font-medium">
                          {eulaUser.eula_accepted_at ? formatTimestamp(eulaUser.eula_accepted_at) : "N/A"}
                        </p>
                      </div>

                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">EULA Version</Label>
                        <p className="mt-1 text-sm text-slate-900 font-medium">
                          {eulaUser.eula_version || "N/A"}
                        </p>
                      </div>

                      {eulaUser.eula_accepted_ip && (
                        <div className="bg-white border border-slate-200 rounded-lg p-4 md:col-span-2">
                          <Label className="text-xs text-slate-500 uppercase font-semibold">IP Address</Label>
                          <p className="mt-1 text-sm text-slate-900 font-mono font-medium">
                            {eulaUser.eula_accepted_ip}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Copy Status */}
                  <div className="pt-6 border-t border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-3 text-lg">EULA Email Copy Status</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">Offered At</Label>
                        <p className="mt-1 text-sm text-slate-900 font-medium">
                          {eulaUser.eula_email_copy_offered_at 
                            ? formatTimestamp(eulaUser.eula_email_copy_offered_at) 
                            : "Not offered"}
                        </p>
                      </div>

                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">User Requested</Label>
                        <div className="mt-1">
                          {eulaUser.eula_email_copy_requested ? (
                            <Badge className="bg-blue-500 text-white">✓ Yes</Badge>
                          ) : (
                            <Badge className="bg-slate-400 text-white">✗ No</Badge>
                          )}
                        </div>
                      </div>

                      {eulaUser.eula_email_copy_requested && (
                        <>
                          <div className="bg-white border border-slate-200 rounded-lg p-4">
                            <Label className="text-xs text-slate-500 uppercase font-semibold">Sent At</Label>
                            <p className="mt-1 text-sm text-slate-900 font-medium">
                              {eulaUser.eula_email_copy_sent_at 
                                ? formatTimestamp(eulaUser.eula_email_copy_sent_at) 
                                : "Not sent"}
                            </p>
                          </div>

                          {eulaUser.eula_email_copy_last_error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 md:col-span-2">
                              <Label className="text-xs text-red-600 uppercase font-semibold">Last Error</Label>
                              <p className="mt-1 text-sm text-red-700 font-mono">
                                {eulaUser.eula_email_copy_last_error}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <div className="text-yellow-600 text-5xl mb-3">⚠️</div>
                  <h4 className="font-semibold text-yellow-900 mb-2">EULA Not Accepted</h4>
                  <p className="text-sm text-yellow-700">
                    This user has not yet accepted the Digital Ink AI™ End User License Agreement.
                    They will be prompted to accept upon their next login.
                  </p>
                </div>
              )}

              {/* Close Button */}
              <div className="mt-6 pt-4 border-t">
                <Button 
                  onClick={() => {
                    setShowEulaModal(false);
                    setEulaUser(null);
                  }}
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Create New User</h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="user@example.com"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <Label htmlFor="tempPassword">Temporary Password *</Label>
                <Input
                  id="tempPassword"
                  type="text"
                  value={newUser.temporary_password}
                  onChange={(e) => setNewUser({...newUser, temporary_password: e.target.value})}
                  placeholder="Enter temporary password (min 8 chars)"
                />
                <p className="text-xs text-slate-500 mt-1">
                  User will use this to login and can change it in their profile
                </p>
              </div>
              
              <div>
                <Label htmlFor="role">User Role</Label>
                <select 
                  id="role"
                  className="w-full p-2 border rounded mt-1"
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                >
                  <option value="free_user">Free User (Default)</option>
                  <option value="a_la_carte">A La Carte</option>
                  <option value="subscriber">Subscriber</option>
                  <option value="student">Student</option>
                  <option value="beta_tester">Beta Tester</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Role can be changed later from Edit User screen
                </p>
              </div>
              
              <div>
                <Label htmlFor="subscriptionStatus">Subscription Status</Label>
                <select 
                  id="subscriptionStatus"
                  className="w-full p-2 border rounded mt-1"
                  value={newUser.subscription_status}
                  onChange={(e) => setNewUser({...newUser, subscription_status: e.target.value})}
                >
                  <option value="None">None (Default)</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Annual">Annual</option>
                  <option value="Student">Student</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Defaults to "None" for new users
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded text-sm border border-blue-200">
                <p className="font-semibold text-blue-900 mb-1">📌 Important:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Share temporary password securely with the user</li>
                  <li>User can change password in their Profile settings</li>
                  <li>Default: Role = Free User, Subscription = None</li>
                </ul>
              </div>
            </div>
            
            <div className="flex space-x-4 mt-6">
              <Button onClick={createUser} className="flex-1">
                Create User
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateUserModal(false);
                  setNewUser({
                    email: "",
                    first_name: "",
                    last_name: "",
                    phone: "",
                    role: "free_user",
                    subscription_status: "None",
                    temporary_password: ""
                  });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Edit User: {selectedUser.first_name} {selectedUser.last_name}</h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="editEmail">Email *</Label>
                <Input
                  id="editEmail"
                  type="email"
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="editFirstName">First Name *</Label>
                  <Input
                    id="editFirstName"
                    value={selectedUser.first_name}
                    onChange={(e) => setSelectedUser({...selectedUser, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="editLastName">Last Name *</Label>
                  <Input
                    id="editLastName"
                    value={selectedUser.last_name}
                    onChange={(e) => setSelectedUser({...selectedUser, last_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="editPhone">Phone</Label>
                <Input
                  id="editPhone"
                  value={selectedUser.phone || ""}
                  onChange={(e) => setSelectedUser({...selectedUser, phone: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="editRole">User Role</Label>
                <select 
                  id="editRole"
                  className="w-full p-2 border rounded mt-1"
                  value={selectedUser.role || "free_user"}
                  onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                >
                  <option value="free_user">Free User (No Payment Method)</option>
                  <option value="a_la_carte">A La Carte (Has Payment Method)</option>
                  <option value="subscriber">Subscriber</option>
                  <option value="student">Student</option>
                  <option value="beta_tester">Beta Tester (Free Unlimited Access)</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Note: Beta Tester gets free unlimited access to all services
                </p>
              </div>
              
              <div>
                <Label htmlFor="editSubscriptionStatus">Subscription Status</Label>
                <select 
                  id="editSubscriptionStatus"
                  className="w-full p-2 border rounded mt-1"
                  value={selectedUser.subscription_status || "None"}
                  onChange={(e) => setSelectedUser({...selectedUser, subscription_status: e.target.value})}
                >
                  <option value="None">None</option>
                  <option value="Monthly">Monthly Subscription</option>
                  <option value="Annual">Annual Subscription</option>
                  <option value="Student">Student Subscription</option>
                  <option value="Enterprise">Enterprise Subscription</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  "None" for both free_user and a_la_carte users
                </p>
              </div>
              
              <div>
                <Label>Payment Method Status</Label>
                <div className="p-2 border rounded mt-1 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">
                      {selectedUser.payment_method ? 
                        "✅ Payment Method On File" : 
                        "❌ No Payment Method"
                      }
                    </span>
                    {selectedUser.payment_method && (
                      <span className="text-xs text-gray-500">
                        {selectedUser.payment_method.brand || "Card"} ****{selectedUser.payment_method.last_four || "****"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Payment method determines free_user vs a_la_carte role
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded text-sm space-y-2">
                <div>
                  <p><strong>User ID:</strong> {selectedUser.id}</p>
                  <p><strong>Created:</strong> {new Date(selectedUser.created_at).toLocaleDateString('en-US', { timeZone: 'America/Denver' })}</p>
                  <p><strong>Email:</strong> {selectedUser.email}</p>
                </div>
                
                {selectedUser.usage_tracking && (
                  <div className="border-t pt-2">
                    <p><strong>BCTG Usage:</strong> {selectedUser.usage_tracking.bctg_monthly_count || 0}/3 monthly uses</p>
                    <p><strong>Remaining Free:</strong> {Math.max(0, 3 - (selectedUser.usage_tracking.bctg_monthly_count || 0))} BCTG uses</p>
                    <p><strong>Total Uses:</strong> BCTG: {selectedUser.usage_tracking.total_bctg_uses || 0}, BVADM: {selectedUser.usage_tracking.total_bvadm_uses || 0}</p>
                  </div>
                )}
                
                <div className="border-t pt-2 text-xs text-gray-600">
                  <p><strong>Testing Guide:</strong></p>
                  <p>• free_user + None = 3 free BCTG, then payment modal</p>
                  <p>• a_la_carte + None = pay-per-use with existing payment</p>
                  <p>• subscriber + Monthly/Annual = unlimited access</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              {/* Quick Test Configurations */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium text-gray-700">Quick Test Configurations:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedUser({
                      ...selectedUser,
                      role: "free_user",
                      subscription_status: "None"
                    })}
                  >
                    🆓 Free User
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedUser({
                      ...selectedUser,
                      role: "a_la_carte",
                      subscription_status: "None"
                    })}
                  >
                    💳 A La Carte
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedUser({
                      ...selectedUser,
                      role: "subscriber",
                      subscription_status: "Monthly"
                    })}
                  >
                    📅 Monthly Sub
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedUser({
                      ...selectedUser,
                      role: "subscriber",
                      subscription_status: "Annual"
                    })}
                  >
                    📅 Annual Sub
                  </Button>
                </div>
              </div>
              
              <div className="flex space-x-4 mt-6">
                <Button onClick={updateUserProfile} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  💾 Save Changes
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowEditUserModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Transaction History Modal */}
      {showUserTransactionsModal && selectedUserForTransactions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold">
                  Transaction History
                </h3>
                <p className="text-sm text-slate-600">
                  {selectedUserForTransactions.first_name} {selectedUserForTransactions.last_name} (ID: {selectedUserForTransactions.id})
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const printContent = document.getElementById('transaction-history-content');
                    const printWindow = window.open('', '', 'height=600,width=800');
                    printWindow.document.write('<html><head><title>Transaction History</title>');
                    printWindow.document.write('<style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background-color:#f8f9fa}h1{color:#333;margin-bottom:10px}.header-info{margin-bottom:20px;padding:10px;background:#f8f9fa;border-radius:4px}.summary{margin-top:20px;padding:15px;background:#e3f2fd;border-radius:4px}</style>');
                    printWindow.document.write('</head><body>');
                    printWindow.document.write(printContent.innerHTML);
                    printWindow.document.write('</body></html>');
                    printWindow.document.close();
                    printWindow.print();
                  }}
                  className="text-slate-600"
                >
                  🖨️ Print
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const element = document.getElementById('transaction-history-content');
                    const fileName = `Transaction_History_${selectedUserForTransactions.first_name}_${selectedUserForTransactions.last_name}_${new Date().toISOString().split('T')[0]}.pdf`;
                    
                    const opt = {
                      margin: 0.5,
                      filename: fileName,
                      image: { type: 'jpeg', quality: 0.98 },
                      html2canvas: { scale: 2, useCORS: true },
                      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
                    };
                    
                    toast.promise(
                      html2pdf().set(opt).from(element).save(),
                      {
                        loading: 'Generating PDF...',
                        success: 'PDF downloaded successfully!',
                        error: 'Failed to generate PDF'
                      }
                    );
                  }}
                  className="text-blue-600"
                >
                  💾 Save PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowUserTransactionsModal(false);
                    setSelectedUserForTransactions(null);
                    setSelectedUserTransactions([]);
                  }}
                >
                  ✕ Close
                </Button>
              </div>
            </div>
            
            <div id="transaction-history-content" style={{padding: '20px', backgroundColor: 'white'}}>
              <div className="mb-4 p-3 bg-slate-50 rounded header-info" style={{marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
                <h1 style={{fontSize: '24px', fontWeight: 'bold', marginBottom: '15px', color: '#333'}}>Transaction History Report</h1>
                <p className="text-sm" style={{marginBottom: '8px'}}><strong>Name:</strong> {selectedUserForTransactions.first_name} {selectedUserForTransactions.last_name}</p>
                <p className="text-sm" style={{marginBottom: '8px'}}><strong>Email:</strong> {selectedUserForTransactions.email}</p>
                <p className="text-sm" style={{marginBottom: '8px'}}><strong>Role:</strong> {selectedUserForTransactions.role}</p>
                <p className="text-sm" style={{marginBottom: '8px'}}><strong>User ID:</strong> {selectedUserForTransactions.id}</p>
                <p className="text-sm" style={{marginBottom: '8px'}}><strong>Generated:</strong> {new Date().toLocaleString('en-US', { timeZone: 'America/Denver', hour12: true })}</p>
              </div>
            
            {loadingUserTransactions ? (
              <div className="text-center py-8">
                <p className="text-slate-600">Loading transactions...</p>
              </div>
            ) : selectedUserTransactions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-600">No transactions found for this user.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" style={{width: '100%', borderCollapse: 'collapse', marginTop: '20px'}}>
                  <thead className="bg-slate-50 border-b" style={{backgroundColor: '#f8f9fa', borderBottom: '2px solid #ddd'}}>
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600 text-sm" style={{textAlign: 'left', padding: '12px', fontWeight: '600'}}>Date</th>
                      <th className="text-left p-3 font-medium text-slate-600 text-sm" style={{textAlign: 'left', padding: '12px', fontWeight: '600'}}>Service</th>
                      <th className="text-left p-3 font-medium text-slate-600 text-sm" style={{textAlign: 'left', padding: '12px', fontWeight: '600'}}>Amount</th>
                      <th className="text-left p-3 font-medium text-slate-600 text-sm" style={{textAlign: 'left', padding: '12px', fontWeight: '600'}}>Status</th>
                      <th className="text-left p-3 font-medium text-slate-600 text-sm" style={{textAlign: 'left', padding: '12px', fontWeight: '600'}}>Transaction ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedUserTransactions.map((transaction, index) => (
                      <tr key={transaction.id || index} className="border-b hover:bg-slate-50" style={{borderBottom: '1px solid #ddd'}}>
                        <td className="p-3 text-sm" style={{padding: '12px', fontSize: '14px'}}>
                          {formatTimestamp(transaction.created_at)}
                        </td>
                        <td className="p-3 text-sm font-medium" style={{padding: '12px', fontSize: '14px', fontWeight: '500'}}>
                          {transaction.service}
                        </td>
                        <td className="p-3 text-sm font-semibold" style={{padding: '12px', fontSize: '14px', fontWeight: '600'}}>
                          ${transaction.amount ? transaction.amount.toFixed(2) : '0.00'}
                        </td>
                        <td className="p-3 text-sm" style={{padding: '12px', fontSize: '14px'}}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: transaction.payment_status === 'paid' ? '#d4edda' : 
                                           transaction.payment_status === 'pending' ? '#fff3cd' : '#e2e3e5',
                            color: transaction.payment_status === 'paid' ? '#155724' :
                                  transaction.payment_status === 'pending' ? '#856404' : '#383d41'
                          }}>
                            {transaction.payment_status}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-slate-500 font-mono" style={{padding: '12px', fontSize: '12px', fontFamily: 'monospace'}}>
                          {transaction.id ? transaction.id.substring(0, 8) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                <div className="mt-4 p-4 bg-blue-50 rounded summary" style={{marginTop: '20px', padding: '20px', backgroundColor: '#e3f2fd', borderRadius: '4px'}}>
                  <div className="grid grid-cols-2 gap-4 text-sm" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                    <div>
                      <p className="font-semibold text-slate-700" style={{fontWeight: '600', marginBottom: '8px'}}>Total Transactions:</p>
                      <p className="text-lg font-bold text-blue-600" style={{fontSize: '24px', fontWeight: 'bold', color: '#1976d2'}}>{selectedUserTransactions.length}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700" style={{fontWeight: '600', marginBottom: '8px'}}>Total Amount:</p>
                      <p className="text-lg font-bold text-blue-600" style={{fontSize: '24px', fontWeight: 'bold', color: '#1976d2'}}>
                        ${selectedUserTransactions.reduce((sum, t) => sum + (t.amount || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Release Manager Tab */}
      {activeTab === "release_manager" && (
        <ReleaseManager user={user} />
      )}

      {/* Database Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full border-4 border-red-500">
            <div className="text-center mb-4">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-red-900 mb-2">
                CRITICAL WARNING
              </h3>
              <div className="bg-red-100 border-2 border-red-500 rounded p-4 mb-4">
                <p className="text-red-900 font-bold mb-3">
                  You are about to PERMANENTLY DELETE all data from the database!
                </p>
                <p className="text-sm text-red-800 mb-2">This will remove:</p>
                <ul className="text-sm text-red-800 text-left ml-6 space-y-1">
                  <li>• All users except rdbrunet@gmail.com</li>
                  <li>• ALL financial transactions & revenue data</li>
                  <li>• ALL service usage records</li>
                  <li>• All BVADM & BCTG reports</li>
                  <li>• All activity logs</li>
                  <li>• Your usage counters (reset to zero)</li>
                </ul>
                <p className="text-xs text-red-700 mt-3 font-semibold">
                  ⚠️ DEVELOPMENT/TESTING ONLY - Never use in production!
                </p>
              </div>
            </div>

            <div className="mb-6">
              <Label className="font-bold text-red-900 mb-2 block">
                Type "RESET DATABASE" to confirm:
              </Label>
              <Input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="Type exactly: RESET DATABASE"
                className="border-red-500 focus:border-red-700 text-center font-mono"
              />
              <p className="text-xs text-red-600 mt-2 text-center">
                Must match exactly (case-sensitive)
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowResetModal(false);
                  setResetConfirmText('');
                }}
                disabled={resetting}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDatabaseReset}
                disabled={resetting || resetConfirmText !== 'RESET DATABASE'}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {resetting ? 'Resetting...' : 'CONFIRM RESET'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h2 className="text-2xl font-bold mb-4 text-red-600">⚠️ Refund Transaction</h2>
            
            {/* Transaction Details */}
            <div className="mb-6 p-4 bg-slate-50 rounded">
              <h3 className="font-semibold mb-2">Transaction Details</h3>
              <div className="text-sm space-y-1">
                <p><span className="font-medium">User:</span> {selectedTransaction.user_name} ({selectedTransaction.user_email})</p>
                <p><span className="font-medium">Service:</span> {selectedTransaction.service}</p>
                <p><span className="font-medium">Amount:</span> <span className="text-lg font-bold text-green-600">${(selectedTransaction.amount || 0).toFixed(2)}</span></p>
                <p><span className="font-medium">Date:</span> {selectedTransaction.created_at_display || formatTimestamp(selectedTransaction.created_at)}</p>
                <p><span className="font-medium">Transaction ID:</span> <span className="font-mono text-xs">{selectedTransaction.id}</span></p>
                {selectedTransaction.stripe_payment_intent_id && (
                  <p><span className="font-medium">Stripe Payment ID:</span> <span className="font-mono text-xs text-blue-600">{selectedTransaction.stripe_payment_intent_id}</span></p>
                )}
                {!selectedTransaction.stripe_payment_intent_id && selectedTransaction.simulated && (
                  <p><span className="font-medium text-orange-600">⚠️ Simulated Transaction</span> <span className="text-xs text-slate-500">(No real Stripe charge)</span></p>
                )}
              </div>
            </div>

            {/* Admin Verification */}
            <div className="mb-4">
              <Label className="text-red-600">Admin Full Name (Required for Audit) *</Label>
              <Input
                type="text"
                placeholder="Enter your full name"
                value={refundData.adminName}
                onChange={(e) => setRefundData({...refundData, adminName: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Refund Reason */}
            <div className="mb-4">
              <Label className="text-red-600">Refund Reason (Required) *</Label>
              <textarea
                placeholder="Explain why this transaction is being refunded..."
                value={refundData.refundReason}
                onChange={(e) => setRefundData({...refundData, refundReason: e.target.value})}
                className="w-full p-2 border rounded mt-1 h-24"
                required
              />
            </div>

            {/* Confirmation Checkbox */}
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={refundData.confirmed}
                  onChange={(e) => setRefundData({...refundData, confirmed: e.target.checked})}
                  className="mt-1"
                />
                <span className="text-sm">
                  I confirm that I want to refund this transaction. This will:
                  <ul className="list-disc ml-5 mt-1">
                    <li>Process a full Stripe refund (${(selectedTransaction.amount || 0).toFixed(2)})</li>
                    <li>Create a credit transaction record</li>
                    <li>Restore user's points/usage credits</li>
                    <li>Send refund confirmation email to user</li>
                    <li>Log admin name and reason for audit trail</li>
                  </ul>
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundData({ adminName: "", refundReason: "", confirmed: false });
                  setSelectedTransaction(null);
                }}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRefund}
                disabled={loading || !refundData.adminName || !refundData.refundReason || !refundData.confirmed}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? 'Processing Refund...' : 'Process Refund'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Payment Details Modal */}
      {showStripeModal && selectedStripeTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl">
                    💳
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Stripe Payment Details</h3>
                    <p className="text-sm text-slate-600">Complete transaction information</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowStripeModal(false)}
                  className="text-slate-400 hover:text-slate-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Transaction Overview */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Transaction Amount</p>
                    <p className="text-3xl font-bold text-blue-600">${(selectedStripeTransaction.amount || 0).toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600 mb-1">Status</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedStripeTransaction.payment_status === 'paid' || selectedStripeTransaction.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : selectedStripeTransaction.payment_status === 'refunded'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {selectedStripeTransaction.payment_status || selectedStripeTransaction.status || 'unknown'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stripe Payment Information */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-slate-800">Stripe Payment Information</h4>
                <div className="space-y-3 bg-slate-50 p-4 rounded-lg">
                  <div className="flex justify-between py-2 border-b border-slate-200">
                    <span className="text-sm font-medium text-slate-600">Stripe Payment Intent ID:</span>
                    <span className="text-sm font-mono text-blue-600 break-all max-w-xs text-right">
                      {selectedStripeTransaction.stripe_payment_intent_id || 'N/A'}
                    </span>
                  </div>
                  {selectedStripeTransaction.stripe_payment_intent_id && (
                    <div className="flex justify-between py-2">
                      <span className="text-sm font-medium text-slate-600">View in Stripe Dashboard:</span>
                      <a
                        href={`https://dashboard.stripe.com/payments/${selectedStripeTransaction.stripe_payment_intent_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        Open in Stripe →
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Transaction Details */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-slate-800">Transaction Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-3 rounded">
                    <p className="text-xs text-slate-600 mb-1">Transaction ID</p>
                    <p className="text-sm font-mono break-all">{selectedStripeTransaction.id}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded">
                    <p className="text-xs text-slate-600 mb-1">Service</p>
                    <p className="text-sm font-semibold">{selectedStripeTransaction.service}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded">
                    <p className="text-xs text-slate-600 mb-1">Date & Time</p>
                    <p className="text-sm">{selectedStripeTransaction.created_at_display || formatTimestamp(selectedStripeTransaction.created_at)}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded">
                    <p className="text-xs text-slate-600 mb-1">Amount</p>
                    <p className="text-sm font-bold text-green-600">${(selectedStripeTransaction.amount || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3 text-slate-800">Customer Information</h4>
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">User Email:</span>
                    <span className="text-sm font-medium">{selectedStripeTransaction.user_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">User ID:</span>
                    <span className="text-sm font-mono text-xs">{selectedStripeTransaction.user_id}</span>
                  </div>
                  {selectedStripeTransaction.user_name && (
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">User Name:</span>
                      <span className="text-sm font-medium">{selectedStripeTransaction.user_name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Metadata */}
              {selectedStripeTransaction.description && (
                <div className="mb-6">
                  <h4 className="font-semibold text-lg mb-3 text-slate-800">Description</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm">{selectedStripeTransaction.description}</p>
                  </div>
                </div>
              )}

              {/* Metadata */}
              {selectedStripeTransaction.metadata && Object.keys(selectedStripeTransaction.metadata).length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-lg mb-3 text-slate-800">Additional Metadata</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    {Object.entries(selectedStripeTransaction.metadata).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-1">
                        <span className="text-sm text-slate-600">{key}:</span>
                        <span className="text-sm font-medium">{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warning for Simulated Transactions */}
              {selectedStripeTransaction.simulated && (
                <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="font-semibold text-orange-800">Simulated Transaction</p>
                      <p className="text-sm text-orange-600 mt-1">
                        This transaction was simulated for testing purposes. No real Stripe charge was made.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={() => setShowStripeModal(false)}
                  className="bg-slate-600 hover:bg-slate-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;